import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import {
  Add,
  Assignment,
  CheckCircle,
  DeleteOutline,
  Engineering,
  KeyboardArrowDown,
  KeyboardArrowUp,
  PictureAsPdf,
  Refresh,
  Search
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { downloadAuthenticatedPdf } from '../utils/authenticatedPdf';
import { ROLES } from '../constants/roles';
import { MRO_COMPONENT_TAGS, DEFAULT_MRO_COMPONENT_TAG } from '../constants/mroComponentTags';
import { useMaintenancePermissions } from './maintenance/hooks/useMaintenancePermissions';
import { getNextAction } from './maintenance/MaintenanceWorkflowStatusCard';
import EngineeringDefectReportDialog from './maintenance/EngineeringDefectReportDialog';
import WorkOrderSecondPageDialog from './maintenance/WorkOrderSecondPageDialog';
import WorkOrderIssuedPartsPanel from './maintenance/components/WorkOrderIssuedPartsPanel';
import AMPChecksTab from './maintenance/components/AMPChecksTab';
import MroQmPendingWorkOrderDetails from './maintenance/MroQmPendingWorkOrderDetails';
import PlanningActions from './maintenance/PlanningActions';
import MaintenanceWorkflowStatusCard from './maintenance/MaintenanceWorkflowStatusCard';
import MROHandoverDialog from './maintenance/MROHandoverDialog';
import MROAssignEngineersDialog from './maintenance/MROAssignEngineersDialog';
import ComponentForm from './components/ComponentForm';
import { buildComponentFormDataFromRecord } from './AircraftLifecycle/utils/componentFormData';
import WorkflowGuide from './common/WorkflowGuide';
import { getWorkOrderDisplayNumber, getWorkOrderCandidateNumber, CONTEXT } from '../utils/workOrderDisplayNumber';
import { getPlanningTask } from '../utils/planningTasks';
const PENDING_TAB_PLAN = 3;

const woDisplayLabel = (wo) => getWorkOrderDisplayNumber(wo, CONTEXT.MRO) || wo?.title || wo?._id || '';
const woSearchText = (wo) => [
  getWorkOrderDisplayNumber(wo, CONTEXT.MRO),
  wo?.mroJobNumber,
  wo?.workOrderNumber,
  wo?.title
].filter(Boolean).join(' ');

const mroReferenceNumber = (wo) =>
  (wo?.mroJobNumber && String(wo.mroJobNumber).trim())
  || getWorkOrderDisplayNumber(wo, CONTEXT.MRO)
  || '-';

const camoWorkOrderNumber = (wo) =>
  (wo?.workOrderNumber && String(wo.workOrderNumber).trim())
  || getWorkOrderCandidateNumber(wo, CONTEXT.MRO)
  || '-';

const renderMroReferenceCell = (wo) => {
  const checklistTotal = Array.isArray(wo?.ampChecks) ? wo.ampChecks.length : 0;
  const checklistDone = checklistTotal
    ? wo.ampChecks.filter((c) => c.status === 'COMPLETED').length
    : 0;
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
      <Typography component="span" variant="body2">{mroReferenceNumber(wo)}</Typography>
      {checklistTotal > 0 ? (
        <Chip
          size="small"
          color={checklistDone === checklistTotal ? 'success' : 'warning'}
          variant="outlined"
          label={`Checklist ${checklistDone}/${checklistTotal}`}
        />
      ) : null}
    </Stack>
  );
};

const renderCamoWorkOrderCell = (wo) => (
  <Typography component="span" variant="body2">{camoWorkOrderNumber(wo)}</Typography>
);

const formatDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toISOString().slice(0, 10);
};

const normalize = (value) => `${value || ''}`.trim().toLowerCase();

const sortRows = (rows, getValue, order = 'asc') => {
  const direction = order === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => {
    const av = normalize(getValue(a));
    const bv = normalize(getValue(b));
    if (av < bv) return -1 * direction;
    if (av > bv) return 1 * direction;
    return 0;
  });
};

const isInMroPipeline = (wo) => {
  if (!wo || wo.status === 'CANCELLED' || !wo.planningApproved) return false;
  if (wo.needsMROAction || wo.mroAccepted) return true;
  if (wo.mroEngineerCompleted) return true;
  if (wo.status === 'HOLD' && wo.linkedWorkOrderId) return true;
  return false;
};

const MARK_COMPLETE_ROLES = [ROLES.MRO_MANAGER, ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER];

const canMarkWorkOrderCompleted = (wo, role) => {
  if (wo?.standaloneEmergencyRepair) return false;
  if (!MARK_COMPLETE_ROLES.includes(role)) return false;
  if (!wo?.mroEngineerCompleted || !wo?.mroQmCompletionAcknowledged || wo?.status !== 'IN_PROGRESS') {
    return false;
  }
  const external = wo?.jobSource === 'EXTERNAL_API' || wo?.jobSource === 'EXTERNAL_MANUAL' || wo?.jobSource === 'EXTERNAL_PORTAL';
  if (external) {
    // AUTO_AFTER_MRO_QM (server also auto-sets camoQmCompletionAcknowledged)
    return true;
  }
  return Boolean(wo?.camoQmCompletionAcknowledged);
};

const isPendingManagerAction = (wo, user, permissions) =>
  Boolean(permissions.canMROAction(wo) || canMarkWorkOrderCompleted(wo, user?.role));
const isPendingEngineerAction = (wo, user, permissions) =>
  Boolean(permissions.canMROEngineerComplete(wo));
const isPendingMroQmAction = (wo, _user, permissions) =>
  Boolean(permissions.canMROQMCompletionAck(wo));

const getDefaultPendingTab = (role) => {
  if (role === ROLES.PLANNING_MANAGER || role === ROLES.ACCOUNTABLE_MANAGER) return PENDING_TAB_PLAN;
  if (role === ROLES.MRO_ENGINEER) return 1;
  if (role === ROLES.MRO_QM_MANAGER || role === ROLES.MRO_QM_OFFICER) return 2;
  return 0;
};

const getManagerPendingLabel = (wo, user, permissions) => {
  if (permissions.canMROAction(wo)) return 'MRO acceptance';
  if (canMarkWorkOrderCompleted(wo, user?.role)) return 'Mark work order completed';
  return null;
};
const getEngineerPendingLabel = (wo, _user, permissions) => {
  if (permissions.canMROEngineerComplete(wo)) return 'MRO engineer completion';
  return null;
};
const getMroQmPendingLabel = (wo, _user, permissions) => {
  if (permissions.canMROQMCompletionAck(wo)) return 'MRO QM completion acknowledgment';
  return null;
};

const EMPTY_MANAGER_FORM = {
  decision: 'ACCEPT',
  notes: '',
  assignedEngineerIds: [],
  currentEngineerId: '',
  assignedDate: '',
  loading: false,
  error: ''
};

const engineerDisplayName = (engineer) => {
  if (!engineer) return '';
  if (typeof engineer === 'string') return engineer;
  return `${engineer.firstName || ''} ${engineer.lastName || ''}`.trim() || engineer.email || engineer._id || '';
};

const idOfUser = (value) => (value && typeof value === 'object' ? String(value._id || '') : String(value || ''));
const EMPTY_ENGINEER_FORM = { decision: 'COMPLETE', notes: '', remarks: '', loading: false, error: '', crsFile: null, crsUploading: false };
const EMPTY_MRO_QM_FORM = { decision: 'ACKNOWLEDGE', notes: '', remarks: '', loading: false, error: '' };
const EMPTY_COMPONENT_SWAP_FORM = {
  lookupPartNumber: '',
  replacementPartNumber: '',
  currentSerialNumber: '',
  replacementSerialNumber: '',
  replacementTimeSinceRecords: [],
  showAddComponentSetup: false,
  addLookupPartNumber: '',
  addLookupLoading: false,
  addReferenceComponent: null,
  addPartNumber: '',
  addSerialNumber: '',
  addTimeSinceRecords: [],
  operationQueue: [],
  lookupLoading: false,
  actionLoading: false,
  downloadingComponentPdf: false,
  downloadingRemovalCertPdf: false,
  downloadingMroTagPdf: false,
  selectedTagType: DEFAULT_MRO_COMPONENT_TAG,
  tagReason: '',
  tagRemarks: '',
  currentComponent: null,
  error: ''
};

const EMPTY_TSR_RECORD = {
  type: 'TSN',
  label: 'Since New (TSN)',
  elapsedTime: { value: '00:00', unit: 'Hours' },
  limit: { value: '00:00', unit: 'Hours' },
  remainingTime: { value: '00:00', unit: 'Hours' },
  startDate: new Date().toISOString().split('T')[0],
  asOnDate: new Date().toISOString().split('T')[0]
};

const normalizePartNumber = (value) => String(value || '').trim().toUpperCase();

const getId = (value) => (value && typeof value === 'object' ? (value._id || value.id || '') : value || '');

const EMPTY_COMPONENT_FORM_DROPDOWNS = {
  aircraft: [],
  ataChapters: [],
  assemblyTypes: [],
  manufacturerNames: [],
  models: [],
  licenses: [],
  parts: [],
  monitorTypes: []
};

const generateNextComponentCode = (currentCode, existingCodes = []) => {
  const normalizedCurrent = String(currentCode || '').trim().toUpperCase();
  const match = normalizedCurrent.match(/^(.+)-(\d+)$/);
  if (!match) {
    return `${normalizedCurrent || 'CMP'}-${Date.now().toString().slice(-4)}`;
  }
  const prefix = match[1];
  const all = new Set(existingCodes.map((c) => String(c || '').trim().toUpperCase()));
  let next = parseInt(match[2], 10) + 1;
  let candidate = `${prefix}-${String(next).padStart(match[2].length, '0')}`;
  while (all.has(candidate)) {
    next += 1;
    candidate = `${prefix}-${String(next).padStart(match[2].length, '0')}`;
  }
  return candidate;
};

const PendingWorkOrderDetails = ({
  wo,
  user,
  permissions,
  managerForm,
  engineerForm,
  mroQmForm,
  mroEngineers,
  onManagerFieldChange,
  onEngineerFieldChange,
  onMroQmFieldChange,
  onSubmitManagerAction,
  onSubmitEngineerAction,
  onSubmitMroQmAction,
  onSubmitCompleteAction,
  onUploadEngineerCrs,
  onDownloadEngineerCrs,
  onOpenHandover,
  onOpenAssignEngineers,
  componentSwapForm,
  onComponentSwapFieldChange,
  onQueueComponentOperation,
  onRemoveQueuedComponentOperation,
  onExecuteQueuedComponentOperations,
  onToggleAddComponentSetup,
  onOpenAddReferenceComponentForm,
  onAddComponentFieldChange,
  onOpenAddComponentForm,
  onQueueAddComponent,
  onOpenComponentPageByPartNumber,
  onDownloadComponentPartDetailPdf,
  onDownloadComponentRemovalCertificatePdf,
  onDownloadMroComponentTagPdf,
  onOpenReplacementComponentPage,
  onExecuteComponentSwap,
  tlbData,
  tlbLoading,
  tlbSaving,
  tlbError,
  onTlbFieldChange,
  onSaveTlbTotals,
  onAmpChecksUpdated
}) => {
  const next = getNextAction(wo, user?.role);
  const currentEngineer = wo.mroCurrentEngineerId || wo.mroAssignedEngineerId;
  const team = Array.isArray(wo.mroAssignedEngineerIds) && wo.mroAssignedEngineerIds.length
    ? wo.mroAssignedEngineerIds
    : (wo.mroAssignedEngineerId ? [wo.mroAssignedEngineerId] : []);

  return (
    <Box sx={{ py: 1 }}>
      <Typography variant="body2">
        <strong>Task details:</strong> {next?.description || 'No additional details.'}
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
        <Chip size="small" variant="outlined" label={`Scheduled: ${formatDate(wo.scheduledDate || wo.dueDate)}`} />
        <Chip size="small" variant="outlined" label={`MRO needed: ${wo.needsMROAction ? 'Yes' : 'No'}`} />
        <Chip size="small" variant="outlined" label={`MRO accepted: ${wo.mroAccepted ? 'Yes' : 'No'}`} />
        <Chip size="small" variant="outlined" label={`Engineer completed: ${wo.mroEngineerCompleted ? 'Yes' : 'No'}`} />
        <Chip size="small" variant="outlined" label={`MRO QM acknowledged: ${wo.mroQmCompletionAcknowledged ? 'Yes' : 'No'}`} />
        {currentEngineer && (
          <Chip size="small" color="primary" variant="outlined" label={`Current: ${engineerDisplayName(currentEngineer)}`} />
        )}
        {team.length > 1 && (
          <Chip size="small" variant="outlined" label={`Team: ${team.length} engineers`} />
        )}
        {(wo.operatorCode || wo.operatorName) && (
          <Chip size="small" color="info" variant="outlined" label={`Operator: ${wo.operatorCode || wo.operatorName}`} />
        )}
        {(wo.jobSource === 'EXTERNAL_API' || wo.jobSource === 'EXTERNAL_MANUAL' || wo.jobSource === 'EXTERNAL_PORTAL') && (
          <Chip size="small" color="warning" variant="outlined" label={`Source: ${wo.jobSource}`} />
        )}
      </Stack>

      <WorkOrderIssuedPartsPanel workOrderId={wo._id} />

      {Array.isArray(wo.ampChecks) && wo.ampChecks.length > 0 && (
        <Box sx={{ mt: 1.5, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
            sx={{ mb: 1 }}
          >
            <Typography variant="subtitle2">
              Checklist for MRO Engineer ({wo.ampChecks.filter((c) => c.status === 'COMPLETED').length}/{wo.ampChecks.length} completed — required before engineer complete)
            </Typography>
            {permissions.canMROHandover?.(wo) && wo.ampChecks.some((c) => c.status !== 'COMPLETED') && (
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                onClick={() => onOpenHandover?.(wo)}
                sx={{ flexShrink: 0 }}
              >
                Hand over at checks
              </Button>
            )}
          </Stack>
          <AMPChecksTab workOrder={wo} onUpdate={onAmpChecksUpdated} user={user} title="Checklist" />
        </Box>
      )}

      <Box sx={{ mt: 1.5, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Last approved technical logbook total carried forward</Typography>
        {tlbLoading ? (
          <Typography variant="caption" color="text.secondary">Loading latest approved totals...</Typography>
        ) : !tlbData?.entryId ? (
          <Alert severity="warning">No approved technical logbook entry with carried-forward totals found for this aircraft.</Alert>
        ) : (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Entry ID: {tlbData.entryId}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
              {Object.keys(tlbData.totals || {}).length === 0 ? (
                <Typography variant="caption" color="text.secondary">No numeric carried-forward fields found.</Typography>
              ) : Object.entries(tlbData.totals).map(([key, value]) => (
                <TextField
                  key={`${wo._id}-${key}`}
                  size="small"
                  type="number"
                  label={key}
                  value={value ?? 0}
                  onChange={(e) => onTlbFieldChange(wo, key, e.target.value)}
                  sx={{ minWidth: 180 }}
                />
              ))}
            </Stack>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Button variant="outlined" size="small" disabled={tlbSaving || !tlbData.entryId} onClick={() => onSaveTlbTotals(wo)}>
                {tlbSaving ? 'Saving...' : 'Save Total Carried Forward'}
              </Button>
            </Stack>
            {tlbError && <Alert severity="error" sx={{ mt: 1 }}>{tlbError}</Alert>}
          </>
        )}
      </Box>

      {(permissions.canMROAction(wo) || permissions.canMROEngineerComplete(wo) || permissions.canMROQMCompletionAck(wo) || permissions.canMROAssignEngineers?.(wo) || permissions.canMROHandover?.(wo) || canMarkWorkOrderCompleted(wo, user?.role)) && (
        <Box sx={{ mt: 1.5, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Workflow actions</Typography>

          {permissions.canMROAction(wo) && (
            <Stack spacing={1} sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary">MRO manager action</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField
                  select
                  size="small"
                  label="Decision"
                  value={managerForm.decision}
                  onChange={(e) => onManagerFieldChange(wo._id, 'decision', e.target.value)}
                  sx={{ minWidth: 130 }}
                >
                  <MenuItem value="ACCEPT">Accept</MenuItem>
                  <MenuItem value="REJECT">Reject</MenuItem>
                </TextField>
                <TextField
                  size="small"
                  label="Notes"
                  value={managerForm.notes}
                  onChange={(e) => onManagerFieldChange(wo._id, 'notes', e.target.value)}
                  placeholder="Required"
                  fullWidth
                />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
                <FormControl size="small" sx={{ minWidth: 260, flex: 1 }}>
                  <InputLabel id={`mro-team-${wo._id}`}>Assign engineers (optional)</InputLabel>
                  <Select
                    labelId={`mro-team-${wo._id}`}
                    multiple
                    value={managerForm.assignedEngineerIds || []}
                    onChange={(e) => {
                      const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                      onManagerFieldChange(wo._id, 'assignedEngineerIds', value);
                      const current = managerForm.currentEngineerId;
                      if (current && !value.includes(current)) {
                        onManagerFieldChange(wo._id, 'currentEngineerId', value[0] || '');
                      } else if (!current && value.length) {
                        onManagerFieldChange(wo._id, 'currentEngineerId', value[0]);
                      }
                    }}
                    input={<OutlinedInput label="Assign engineers (optional)" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((id) => {
                          const eng = mroEngineers.find((e) => e._id === id);
                          return <Chip key={id} size="small" label={eng ? engineerDisplayName(eng) : id} />;
                        })}
                      </Box>
                    )}
                  >
                    {mroEngineers.map((eng) => (
                      <MenuItem key={eng._id} value={eng._id}>
                        {engineerDisplayName(eng)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {(managerForm.assignedEngineerIds || []).length > 0 && (
                  <TextField
                    select
                    size="small"
                    label="Current shift engineer"
                    value={managerForm.currentEngineerId || managerForm.assignedEngineerIds[0] || ''}
                    onChange={(e) => onManagerFieldChange(wo._id, 'currentEngineerId', e.target.value)}
                    sx={{ minWidth: 200 }}
                  >
                    {managerForm.assignedEngineerIds.map((id) => {
                      const eng = mroEngineers.find((e) => e._id === id);
                      return (
                        <MenuItem key={id} value={id}>
                          {eng ? engineerDisplayName(eng) : id}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                )}
                <TextField
                  size="small"
                  type="date"
                  label="Assign date"
                  value={managerForm.assignedDate}
                  onChange={(e) => onManagerFieldChange(wo._id, 'assignedDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <Button
                  variant="contained"
                  color={managerForm.decision === 'REJECT' ? 'error' : 'primary'}
                  disabled={managerForm.loading || !managerForm.notes.trim()}
                  onClick={() => onSubmitManagerAction(wo)}
                >
                  {managerForm.loading ? 'Submitting...' : managerForm.decision === 'REJECT' ? 'Reject' : 'Accept'}
                </Button>
              </Stack>
              {managerForm.error && <Alert severity="error">{managerForm.error}</Alert>}
            </Stack>
          )}

          {(permissions.canMROAssignEngineers?.(wo) || permissions.canMROHandover?.(wo)) && (
            <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} useFlexGap flexWrap="wrap">
              {permissions.canMROAssignEngineers?.(wo) && (
                <Button size="small" variant="outlined" onClick={() => onOpenAssignEngineers?.(wo)}>
                  Manage engineer team
                </Button>
              )}
              {permissions.canMROHandover?.(wo) && (
                <Button size="small" variant="outlined" color="secondary" onClick={() => onOpenHandover?.(wo)}>
                  {Array.isArray(wo.ampChecks) && wo.ampChecks.some((c) => c.status !== 'COMPLETED')
                    ? 'Hand over at checks'
                    : 'Hand over to next shift'}
                </Button>
              )}
            </Stack>
          )}

          {permissions.canMROEngineerComplete(wo) && (
            <Stack spacing={1} sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary">MRO engineer action</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField
                  select
                  size="small"
                  label="Decision"
                  value={engineerForm.decision}
                  onChange={(e) => onEngineerFieldChange(wo._id, 'decision', e.target.value)}
                  sx={{ minWidth: 140 }}
                >
                  <MenuItem value="COMPLETE">Complete</MenuItem>
                  <MenuItem value="REJECT">Reject</MenuItem>
                </TextField>
                <TextField
                  size="small"
                  label="Notes (optional)"
                  value={engineerForm.notes}
                  onChange={(e) => onEngineerFieldChange(wo._id, 'notes', e.target.value)}
                  fullWidth
                />
              </Stack>
              {engineerForm.decision === 'REJECT' && (
                <TextField
                  size="small"
                  label="Rejection remarks"
                  value={engineerForm.remarks}
                  onChange={(e) => onEngineerFieldChange(wo._id, 'remarks', e.target.value)}
                  required
                  fullWidth
                />
              )}
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  color={engineerForm.decision === 'REJECT' ? 'error' : 'success'}
                  disabled={engineerForm.loading || (engineerForm.decision === 'REJECT' && !engineerForm.remarks.trim())}
                  onClick={() => onSubmitEngineerAction(wo)}
                >
                  {engineerForm.loading ? 'Submitting...' : engineerForm.decision === 'REJECT' ? 'Reject' : 'Mark Completed'}
                </Button>
                <Button
                  variant="outlined"
                  component="label"
                  size="small"
                  disabled={engineerForm.crsUploading}
                >
                  Select CRS
                  <input
                    type="file"
                    hidden
                    onChange={(e) => onEngineerFieldChange(wo._id, 'crsFile', e.target.files?.[0] || null)}
                  />
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={engineerForm.crsUploading || !engineerForm.crsFile}
                  onClick={() => onUploadEngineerCrs(wo)}
                >
                  {engineerForm.crsUploading ? 'Uploading...' : 'Upload CRS'}
                </Button>
                {wo?.crsDocument?.originalName && (
                  <Button variant="outlined" size="small" onClick={() => onDownloadEngineerCrs(wo)}>
                    Download CRS
                  </Button>
                )}
              </Stack>
              {engineerForm.crsFile && (
                <Typography variant="caption" color="text.secondary">
                  Selected CRS: {engineerForm.crsFile.name}
                </Typography>
              )}
              {engineerForm.error && <Alert severity="error">{engineerForm.error}</Alert>}

              <Box sx={{ mt: 1.5, p: 1.25, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Optional component remove / replace
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <TextField
                    size="small"
                    label="Current component part number"
                    value={componentSwapForm.lookupPartNumber}
                    onChange={(e) => onComponentSwapFieldChange(wo._id, 'lookupPartNumber', e.target.value)}
                    fullWidth
                  />
                  <Button
                    variant="outlined"
                    disabled={componentSwapForm.lookupLoading || !componentSwapForm.lookupPartNumber.trim()}
                    onClick={() => onOpenComponentPageByPartNumber(wo)}
                  >
                    {componentSwapForm.lookupLoading ? 'Finding...' : 'Find'}
                  </Button>
                </Stack>

                {componentSwapForm.currentComponent && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Current component: {componentSwapForm.currentComponent.code || '-'} | PN {componentSwapForm.currentComponent.partNumber || '-'} | SN {componentSwapForm.currentComponent.serialNumber || '-'}
                  </Alert>
                )}

                {componentSwapForm.currentComponent?._id && (
                  <Stack spacing={1} sx={{ mt: 1.5, p: 1.25, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Print GETS hangar tag (exact manual layout) or full removal certificate — operator letterpad
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                      <TextField
                        select
                        size="small"
                        label="Document to generate"
                        value={componentSwapForm.selectedTagType || DEFAULT_MRO_COMPONENT_TAG}
                        onChange={(e) => onComponentSwapFieldChange(wo._id, 'selectedTagType', e.target.value)}
                        sx={{ minWidth: 300 }}
                      >
                        {MRO_COMPONENT_TAGS.map((tag) => (
                          <MenuItem key={tag.key} value={tag.key}>
                            {tag.isCertificate ? tag.label : `${tag.label} (${tag.formNo})`}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        size="small"
                        label="Reason (optional)"
                        value={componentSwapForm.tagReason || ''}
                        onChange={(e) => onComponentSwapFieldChange(wo._id, 'tagReason', e.target.value)}
                        fullWidth
                      />
                      <TextField
                        size="small"
                        label="Remarks (optional)"
                        value={componentSwapForm.tagRemarks || ''}
                        onChange={(e) => onComponentSwapFieldChange(wo._id, 'tagRemarks', e.target.value)}
                        fullWidth
                      />
                      <Button
                        variant="contained"
                        color="secondary"
                        startIcon={componentSwapForm.downloadingMroTagPdf ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdf />}
                        disabled={componentSwapForm.downloadingMroTagPdf || componentSwapForm.lookupLoading}
                        onClick={() => onDownloadMroComponentTagPdf(wo)}
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        {componentSwapForm.downloadingMroTagPdf ? 'Generating…' : 'Generate tag PDF'}
                      </Button>
                    </Stack>
                  </Stack>
                )}

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
                  <TextField
                    size="small"
                    label="Replacement part number"
                    value={componentSwapForm.replacementPartNumber}
                    onChange={(e) => onComponentSwapFieldChange(wo._id, 'replacementPartNumber', e.target.value)}
                    placeholder="Defaults to current part number"
                    fullWidth
                  />
                  <TextField
                    size="small"
                    label="Current serial number"
                    value={componentSwapForm.currentSerialNumber}
                    InputProps={{ readOnly: true }}
                    sx={{ minWidth: 220 }}
                  />
                  <TextField
                    size="small"
                    label="New serial number (replacement)"
                    value={componentSwapForm.replacementSerialNumber}
                    onChange={(e) => onComponentSwapFieldChange(wo._id, 'replacementSerialNumber', e.target.value)}
                    placeholder="Enter replacement serial"
                    sx={{ minWidth: 220 }}
                  />
                  <Button
                    variant="contained"
                    color="warning"
                    disabled={componentSwapForm.actionLoading || !componentSwapForm.currentComponent}
                    onClick={() => onExecuteComponentSwap(wo, 'REMOVE')}
                  >
                    {componentSwapForm.actionLoading ? 'Processing...' : 'Remove'}
                  </Button>
                  <Button
                    variant="outlined"
                    color="warning"
                    disabled={componentSwapForm.actionLoading || !componentSwapForm.currentComponent}
                    onClick={() => onQueueComponentOperation(wo, 'REMOVE')}
                  >
                    Queue Remove
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={componentSwapForm.lookupLoading || !componentSwapForm.lookupPartNumber.trim()}
                    onClick={() => onOpenReplacementComponentPage(wo)}
                  >
                    {componentSwapForm.lookupLoading ? 'Loading...' : 'Replace'}
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    disabled={componentSwapForm.actionLoading || !componentSwapForm.currentComponent}
                    onClick={() => onQueueComponentOperation(wo, 'REPLACE')}
                  >
                    Queue Replace
                  </Button>
                </Stack>
                {(componentSwapForm.operationQueue || []).length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Queued component operations
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Action</TableCell>
                            <TableCell>Current PN</TableCell>
                            <TableCell>Current SN</TableCell>
                            <TableCell>New PN</TableCell>
                            <TableCell>New SN</TableCell>
                            <TableCell>TSR Rows</TableCell>
                            <TableCell>Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {componentSwapForm.operationQueue.map((op, idx) => (
                            <TableRow key={`${wo._id}-op-${idx}`}>
                              <TableCell>{op.actionType}</TableCell>
                              <TableCell>{op.currentComponent?.partNumber || '-'}</TableCell>
                              <TableCell>{op.currentSerialNumber || '-'}</TableCell>
                              <TableCell>{op.replacementPartNumber || '-'}</TableCell>
                              <TableCell>{op.replacementSerialNumber || '-'}</TableCell>
                              <TableCell>{Array.isArray(op.replacementTimeSinceRecords) ? op.replacementTimeSinceRecords.length : 0}</TableCell>
                              <TableCell>
                                <IconButton size="small" color="error" onClick={() => onRemoveQueuedComponentOperation(wo._id, idx)}>
                                  <DeleteOutline fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Stack direction="row" justifyContent="flex-end" sx={{ mt: 0.75 }}>
                      <Button
                        variant="contained"
                        color="secondary"
                        disabled={componentSwapForm.actionLoading}
                        onClick={() => onExecuteQueuedComponentOperations(wo)}
                      >
                        {componentSwapForm.actionLoading ? 'Processing...' : 'Execute Queued Operations'}
                      </Button>
                    </Stack>
                  </Box>
                )}
                <Box sx={{ mt: 1.25, p: 1, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                      Add new component (separate flow)
                    </Typography>
                    <Button size="small" variant="outlined" color="success" onClick={() => onToggleAddComponentSetup(wo._id)}>
                      {componentSwapForm.showAddComponentSetup ? 'Hide Add Setup' : 'Add Component'}
                    </Button>
                  </Stack>
                  {componentSwapForm.showAddComponentSetup && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Add a new component using a reference installed component for assembly and options. Use Find to review the reference, then Add Component to open the new component form.
                      </Typography>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <TextField
                          size="small"
                          label="Reference component part number"
                          value={componentSwapForm.addLookupPartNumber}
                          onChange={(e) => onAddComponentFieldChange(wo._id, 'addLookupPartNumber', e.target.value)}
                          fullWidth
                        />
                        <Button
                          variant="outlined"
                          disabled={componentSwapForm.addLookupLoading || !componentSwapForm.addLookupPartNumber.trim()}
                          onClick={() => onOpenAddReferenceComponentForm(wo)}
                        >
                          {componentSwapForm.addLookupLoading ? 'Finding...' : 'Find'}
                        </Button>
                      </Stack>
                      {componentSwapForm.addReferenceComponent && (
                        <Alert severity="info" sx={{ mt: 1 }}>
                          Reference component: {componentSwapForm.addReferenceComponent.code || '-'} | PN {componentSwapForm.addReferenceComponent.partNumber || '-'} | SN {componentSwapForm.addReferenceComponent.serialNumber || '-'}
                        </Alert>
                      )}
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
                        <TextField
                          size="small"
                          label="New component part number"
                          value={componentSwapForm.addPartNumber}
                          onChange={(e) => onAddComponentFieldChange(wo._id, 'addPartNumber', e.target.value)}
                          placeholder="Defaults to reference part number"
                          fullWidth
                        />
                        <TextField
                          size="small"
                          label="New serial number"
                          value={componentSwapForm.addSerialNumber}
                          onChange={(e) => onAddComponentFieldChange(wo._id, 'addSerialNumber', e.target.value)}
                          placeholder="Enter new serial"
                          sx={{ minWidth: 220 }}
                        />
                        <Button
                          variant="contained"
                          color="success"
                          disabled={componentSwapForm.addLookupLoading || !componentSwapForm.addLookupPartNumber.trim()}
                          onClick={() => onOpenAddComponentForm(wo)}
                        >
                          {componentSwapForm.addLookupLoading ? 'Loading...' : 'Add Component'}
                        </Button>
                        <Button
                          variant="outlined"
                          color="success"
                          disabled={componentSwapForm.actionLoading || !componentSwapForm.addReferenceComponent}
                          onClick={() => onQueueAddComponent(wo)}
                        >
                          Queue Add
                        </Button>
                      </Stack>
                    </Box>
                  )}
                </Box>
                {componentSwapForm.error && <Alert severity="error" sx={{ mt: 1 }}>{componentSwapForm.error}</Alert>}
              </Box>
            </Stack>
          )}

          {permissions.canMROQMCompletionAck(wo) && (
            <Stack spacing={1} sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary">MRO QM action</Typography>
              <Alert severity="info" sx={{ mb: 1 }}>
                Open the <strong>MRO QM</strong> tab in Pending work for the full work order review and Approve / Reject / Back to Engineer actions.
              </Alert>
            </Stack>
          )}

          {canMarkWorkOrderCompleted(wo, user?.role) && (
            <Stack spacing={1}>
              <Typography variant="caption" color="text.secondary">Final completion action</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button variant="contained" color="success" startIcon={<CheckCircle />} onClick={() => onSubmitCompleteAction(wo)}>
                  Mark Work Order Completed
                </Button>
              </Stack>
            </Stack>
          )}
        </Box>
      )}
    </Box>
  );
};

const MroBoard = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const permissions = useMaintenancePermissions(user);

  const [workOrders, setWorkOrders] = useState([]);
  const [pendingPlanning, setPendingPlanning] = useState([]);
  const [mroEngineers, setMroEngineers] = useState([]);
  const [charterAssignments, setCharterAssignments] = useState([]);
  const [charterLoading, setCharterLoading] = useState(false);
  const [openingCharterTlbId, setOpeningCharterTlbId] = useState(null);
  const [charterMroDialogOpen, setCharterMroDialogOpen] = useState(false);
  const [charterMroTrip, setCharterMroTrip] = useState(null);
  const [charterMroForm, setCharterMroForm] = useState({ assignedMroEngineerId: '', remarks: '' });
  const [assigningCharterMro, setAssigningCharterMro] = useState(false);
  const [charterMroEngineers, setCharterMroEngineers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [benchTab, setBenchTab] = useState(() => (user?.role === ROLES.MRO_ENGINEER ? 1 : 0));
  const [pendingTab, setPendingTab] = useState(() => getDefaultPendingTab(user?.role));

  const [pendingManagerSearch, setPendingManagerSearch] = useState('');
  const [pendingManagerStatusFilter, setPendingManagerStatusFilter] = useState('ALL');
  const [pendingManagerSortBy, setPendingManagerSortBy] = useState('workOrder');
  const [pendingManagerSortOrder, setPendingManagerSortOrder] = useState('asc');
  const [pendingEngineerSearch, setPendingEngineerSearch] = useState('');
  const [pendingEngineerStatusFilter, setPendingEngineerStatusFilter] = useState('ALL');
  const [pendingEngineerSortBy, setPendingEngineerSortBy] = useState('workOrder');
  const [pendingEngineerSortOrder, setPendingEngineerSortOrder] = useState('asc');
  const [pendingMroQmSearch, setPendingMroQmSearch] = useState('');
  const [pendingMroQmStatusFilter, setPendingMroQmStatusFilter] = useState('ALL');
  const [pendingMroQmSortBy, setPendingMroQmSortBy] = useState('workOrder');
  const [pendingMroQmSortOrder, setPendingMroQmSortOrder] = useState('asc');
  const [pendingPlanningSearch, setPendingPlanningSearch] = useState('');
  const [pendingPlanningSortBy, setPendingPlanningSortBy] = useState('workOrder');
  const [pendingPlanningSortOrder, setPendingPlanningSortOrder] = useState('asc');

  const [pipelineStatusFilter, setPipelineStatusFilter] = useState('ALL');
  const [pipelineTurnFilter, setPipelineTurnFilter] = useState('ALL');
  const [pipelineSortBy, setPipelineSortBy] = useState('scheduledDate');
  const [pipelineSortOrder, setPipelineSortOrder] = useState('asc');

  const [expandedManagerId, setExpandedManagerId] = useState(null);
  const [expandedEngineerId, setExpandedEngineerId] = useState(null);
  const [expandedMroQmId, setExpandedMroQmId] = useState(null);
  const [expandedPlanningId, setExpandedPlanningId] = useState(null);
  const [planningActionOpen, setPlanningActionOpen] = useState(false);
  const [selectedPlanningWo, setSelectedPlanningWo] = useState(null);
  const [expandedPipelineId, setExpandedPipelineId] = useState(null);
  const [managerForms, setManagerForms] = useState({});
  const [engineerForms, setEngineerForms] = useState({});
  const [mroQmForms, setMroQmForms] = useState({});
  const [mroQmDetailsById, setMroQmDetailsById] = useState({});
  const [mroQmDetailsLoading, setMroQmDetailsLoading] = useState({});
  const [mroQmDetailsError, setMroQmDetailsError] = useState({});
  const [componentSwapForms, setComponentSwapForms] = useState({});
  const [tlbByAircraft, setTlbByAircraft] = useState({});
  const [tlbLoadingByAircraft, setTlbLoadingByAircraft] = useState({});
  const [tlbSavingByAircraft, setTlbSavingByAircraft] = useState({});
  const [tlbErrorByAircraft, setTlbErrorByAircraft] = useState({});
  const [edrOpen, setEdrOpen] = useState(false);
  const [edrWorkOrder, setEdrWorkOrder] = useState(null);
  const [woPage2Open, setWoPage2Open] = useState(false);
  const [woPage2WorkOrder, setWoPage2WorkOrder] = useState(null);
  const [componentFormOpen, setComponentFormOpen] = useState(false);
  const [componentFormSelected, setComponentFormSelected] = useState(null);
  const [componentFormData, setComponentFormData] = useState({});
  const [componentFormAircraft, setComponentFormAircraft] = useState(null);
  const [componentFormAssemblies, setComponentFormAssemblies] = useState([]);
  const [componentFormDropdowns, setComponentFormDropdowns] = useState(EMPTY_COMPONENT_FORM_DROPDOWNS);
  const [componentFormLiveUsage, setComponentFormLiveUsage] = useState(null);
  const [componentFormLinkedWoId, setComponentFormLinkedWoId] = useState(null);
  const [hangarSettings, setHangarSettings] = useState({
    productMode: 'SUITE',
    enableMroFacade: false,
    enableExternalJobs: false
  });
  const [operatorFilter, setOperatorFilter] = useState('ALL');
  const [operators, setOperators] = useState([]);
  const [handoverDialogWo, setHandoverDialogWo] = useState(null);
  const [assignEngineersDialogWo, setAssignEngineersDialogWo] = useState(null);

  const useFacade = Boolean(hangarSettings.enableMroFacade);
  const showCharterTab = hangarSettings.productMode !== 'HANGAR';
  const showPendingPlanTab = useMemo(
    () => hangarSettings.productMode === 'HANGAR'
      && useFacade
      && permissions.canPlan({ camoQmAcknowledged: true, planningApproved: false }),
    [hangarSettings.productMode, useFacade, permissions]
  );
  const showPlanningCreateLink = hangarSettings.productMode === 'HANGAR'
    && Boolean(hangarSettings.enableExternalJobs);

  const openEdr = (wo) => {
    setEdrWorkOrder(wo);
    setEdrOpen(true);
  };
  const closeEdr = () => {
    setEdrOpen(false);
    setEdrWorkOrder(null);
  };
  const openWoPage2 = (wo) => {
    setWoPage2WorkOrder(wo);
    setWoPage2Open(true);
  };
  const closeWoPage2 = () => {
    setWoPage2Open(false);
    setWoPage2WorkOrder(null);
  };

  const closeComponentForm = () => {
    setComponentFormOpen(false);
    setComponentFormSelected(null);
    setComponentFormData({});
    setComponentFormAircraft(null);
    setComponentFormAssemblies([]);
    setComponentFormDropdowns(EMPTY_COMPONENT_FORM_DROPDOWNS);
    setComponentFormLiveUsage(null);
    setComponentFormLinkedWoId(null);
  };

  const fetchComponentFormDropdowns = async () => {
    const [ataChapters, assemblyTypes, manufacturerNames, models, licenses, parts, monitorTypes] = await Promise.all([
      axios.get('/api/master-data/ata-chapters', { params: { isActive: 'true' } }),
      axios.get('/api/master-data/assembly-types', { params: { isActive: 'true' } }),
      axios.get('/api/master-data/manufacturer-names', { params: { isActive: 'true' } }),
      axios.get('/api/master-data/models', { params: { isActive: 'true' } }),
      axios.get('/api/master-data/licenses', { params: { isActive: 'true' } }),
      axios.get('/api/parts', { params: { isActive: 'true', pageSize: 1000, page: 1 } }),
      axios.get('/api/master-data/monitor-types', { params: { isActive: 'true' } })
    ]);
    setComponentFormDropdowns({
      aircraft: [],
      ataChapters: ataChapters.data,
      assemblyTypes: assemblyTypes.data,
      manufacturerNames: manufacturerNames.data,
      models: models.data,
      licenses: licenses.data,
      parts: parts.data.parts || parts.data || [],
      monitorTypes: monitorTypes.data
    });
  };

  const loadComponentFormContext = async (aircraftId, wo) => {
    const aircraft = (typeof wo?.aircraftId === 'object' && wo.aircraftId?._id)
      ? wo.aircraftId
      : { _id: aircraftId };
    const [assembliesRes, liveUsageRes] = await Promise.all([
      axios.get('/api/assemblies', { params: { aircraftId, isActive: 'true' } }),
      axios.get(`/api/aircraft/${aircraftId}/live-usage`).catch(() => ({ data: null }))
    ]);
    setComponentFormAircraft(aircraft);
    setComponentFormAssemblies(Array.isArray(assembliesRes.data) ? assembliesRes.data : []);
    setComponentFormLiveUsage(liveUsageRes.data || null);
    await fetchComponentFormDropdowns();
  };

  const openComponentFormPopup = async ({ wo, componentId, mode, overrides = {} }) => {
    const aircraftId = getAircraftIdFromWo(wo);
    if (!aircraftId || !componentId) return;

    setComponentFormLinkedWoId(wo?._id || null);
    try {
      const [componentRes] = await Promise.all([
        axios.get(`/api/components/${componentId}`)
      ]);
      const component = componentRes.data;
      await loadComponentFormContext(aircraftId, wo);

      if (mode === 'edit') {
        setComponentFormSelected(component);
        setComponentFormData(await buildComponentFormDataFromRecord(component));
      } else {
        setComponentFormSelected(null);
        setComponentFormData(await buildComponentFormDataFromRecord(component, {
          serialNumber: '',
          code: '',
          isActive: true,
          installedOnDate: new Date().toISOString().split('T')[0],
          ...overrides
        }));
      }
      setComponentFormOpen(true);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to open component form', 'error');
    }
  };

  const isMroComponentUser = [ROLES.MRO_ENGINEER, ROLES.MRO_MANAGER].includes(user?.role);

  const handleMroComponentFormSubmit = async (data) => {
    try {
      const linkedWo = workOrders.find((w) => String(w._id) === String(componentFormLinkedWoId))
        || (componentFormLinkedWoId ? { _id: componentFormLinkedWoId } : null);
      const external = linkedWo?.jobSource === 'EXTERNAL_API' || linkedWo?.jobSource === 'EXTERNAL_MANUAL' || linkedWo?.jobSource === 'EXTERNAL_PORTAL';

      // Internal: submit to CAMO approval queue. External: save without CAMO queue (foreign fleet).
      const workbenchMeta = isMroComponentUser && !external
        ? { fromMroWorkbench: true, workbenchWorkOrderId: componentFormLinkedWoId || undefined }
        : {};

      if (componentFormSelected?._id) {
        const wasApproved = componentFormSelected.status === 'APPROVED';
        const res = await axios.put(`/api/components/${componentFormSelected._id}`, {
          ...data,
          ...workbenchMeta
        });
        if (isMroComponentUser && !external && !wasApproved && res.data?.status === 'DRAFT') {
          await axios.post(`/api/components/${componentFormSelected._id}/submit`, {
            fromMroWorkbench: true,
            workOrderId: componentFormLinkedWoId || undefined
          });
        }
        showToast(
          isMroComponentUser
            ? (external
              ? 'Component change saved for external job (no CAMO queue)'
              : 'Component change submitted to CAMO for approval')
            : 'Component updated successfully',
          'success'
        );
        if (componentFormLinkedWoId) {
          const updated = await axios.get(`/api/components/${componentFormSelected._id}`);
          applyFoundComponentToSwapForm(componentFormLinkedWoId, updated.data);
        }
      } else {
        const res = await axios.post('/api/components', data);
        if (isMroComponentUser && !external && res.data?._id) {
          await axios.post(`/api/components/${res.data._id}/submit`, {
            fromMroWorkbench: true,
            workOrderId: componentFormLinkedWoId || undefined
          });
        }
        showToast(
          isMroComponentUser
            ? (external
              ? 'New component saved for external job (no CAMO queue)'
              : 'New component submitted to CAMO for approval')
            : 'Component created successfully',
          'success'
        );
      }
      closeComponentForm();
    } catch (err) {
      showToast(
        err.response?.data?.message || err.response?.data?.errors?.[0]?.msg || 'Error saving component',
        'error'
      );
    }
  };

  const getManagerForm = (id) => managerForms[id] || EMPTY_MANAGER_FORM;
  const getEngineerForm = (id) => engineerForms[id] || EMPTY_ENGINEER_FORM;
  const getMroQmForm = (id) => mroQmForms[id] || EMPTY_MRO_QM_FORM;
  const getComponentSwapForm = (id) => componentSwapForms[id] || EMPTY_COMPONENT_SWAP_FORM;

  const setManagerFormField = (id, key, value) => {
    setManagerForms((prev) => ({ ...prev, [id]: { ...getManagerForm(id), [key]: value } }));
  };
  const setEngineerFormField = (id, key, value) => {
    setEngineerForms((prev) => ({ ...prev, [id]: { ...getEngineerForm(id), [key]: value } }));
  };
  const setMroQmFormField = (id, key, value) => {
    setMroQmForms((prev) => ({ ...prev, [id]: { ...getMroQmForm(id), [key]: value } }));
  };
  const setComponentSwapFormField = (id, key, value) => {
    setComponentSwapForms((prev) => ({ ...prev, [id]: { ...getComponentSwapForm(id), [key]: value } }));
  };
  const toggleAddComponentSetup = (id) => {
    setComponentSwapForms((prev) => ({ ...prev, [id]: { ...getComponentSwapForm(id), showAddComponentSetup: !getComponentSwapForm(id).showAddComponentSetup } }));
  };
  const setAddComponentField = (id, key, value) => {
    setComponentSwapForms((prev) => ({ ...prev, [id]: { ...getComponentSwapForm(id), [key]: value } }));
  };
  const removeQueuedComponentOperation = (id, index) => {
    setComponentSwapForms((prev) => {
      const current = getComponentSwapForm(id);
      const queue = Array.isArray(current.operationQueue) ? current.operationQueue : [];
      return {
        ...prev,
        [id]: { ...current, operationQueue: queue.filter((_, i) => i !== index) }
      };
    });
  };

  const getAircraftIdFromWo = (wo) => wo?.aircraftId?._id || wo?.aircraftId || null;

  const ensureLatestApprovedTlbLoaded = useCallback(async (wo) => {
    const aircraftId = getAircraftIdFromWo(wo);
    if (!aircraftId) return;
    if (tlbByAircraft[aircraftId] || tlbLoadingByAircraft[aircraftId]) return;
    setTlbLoadingByAircraft((prev) => ({ ...prev, [aircraftId]: true }));
    setTlbErrorByAircraft((prev) => ({ ...prev, [aircraftId]: '' }));
    try {
      const res = await axios.get(`/api/technical-log-book/aircraft/${aircraftId}`);
      const entries = Array.isArray(res.data) ? res.data : [];
      const approved = entries
        .filter((e) => e?.workflowStatus === 'APPROVED')
        .sort((a, b) => {
          const at = new Date(a.approvedAt || a.updatedAt || a.createdAt || 0).getTime();
          const bt = new Date(b.approvedAt || b.updatedAt || b.createdAt || 0).getTime();
          return bt - at;
        });
      const latest = approved[0];
      const rawTotals = latest?.totalCarriedForward && typeof latest.totalCarriedForward === 'object'
        ? latest.totalCarriedForward
        : {};
      const numericTotals = Object.entries(rawTotals).reduce((acc, [key, val]) => {
        const n = Number(val);
        if (Number.isFinite(n)) acc[key] = n;
        return acc;
      }, {});
      setTlbByAircraft((prev) => ({
        ...prev,
        [aircraftId]: { entryId: latest?._id || null, totals: numericTotals }
      }));
    } catch (err) {
      setTlbErrorByAircraft((prev) => ({ ...prev, [aircraftId]: err.response?.data?.message || 'Failed to load latest approved technical logbook totals' }));
    } finally {
      setTlbLoadingByAircraft((prev) => ({ ...prev, [aircraftId]: false }));
    }
  }, [tlbByAircraft, tlbLoadingByAircraft]);

  const setTlbField = (wo, key, value) => {
    const aircraftId = getAircraftIdFromWo(wo);
    if (!aircraftId) return;
    const parsed = Number(value);
    setTlbByAircraft((prev) => ({
      ...prev,
      [aircraftId]: {
        ...(prev[aircraftId] || { entryId: null, totals: {} }),
        totals: {
          ...((prev[aircraftId] && prev[aircraftId].totals) || {}),
          [key]: Number.isFinite(parsed) ? parsed : 0
        }
      }
    }));
  };

  const saveTlbTotals = async (wo) => {
    const aircraftId = getAircraftIdFromWo(wo);
    const tlbData = aircraftId ? tlbByAircraft[aircraftId] : null;
    if (!aircraftId || !tlbData?.entryId) {
      showToast('No approved technical logbook entry available to update', 'error');
      return;
    }
    setTlbSavingByAircraft((prev) => ({ ...prev, [aircraftId]: true }));
    setTlbErrorByAircraft((prev) => ({ ...prev, [aircraftId]: '' }));
    try {
      await axios.put(`/api/technical-log-book/${tlbData.entryId}`, {
        totalCarriedForward: tlbData.totals
      });
      showToast('Total carried forward updated successfully', 'success');
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to update total carried forward';
      setTlbErrorByAircraft((prev) => ({ ...prev, [aircraftId]: msg }));
      showToast(msg, 'error');
    } finally {
      setTlbSavingByAircraft((prev) => ({ ...prev, [aircraftId]: false }));
    }
  };

  const fetchWorkOrders = useCallback(async () => {
    setLoading(true);
    try {
      let hangar = hangarSettings;
      try {
        const settingsRes = await axios.get('/api/mro/hangar-settings');
        hangar = settingsRes.data || hangar;
        setHangarSettings(hangar);
      } catch (_) {
        /* facade settings optional when route not yet deployed */
      }

      const facadeOn = Boolean(hangar.enableMroFacade);
      const queueParams = {};
      if (operatorFilter && operatorFilter !== 'ALL') {
        queueParams.operatorId = operatorFilter;
      }

      const [woRes, usersRes, planningRes] = await Promise.all([
        facadeOn
          ? axios.get('/api/mro/queue', { params: queueParams })
          : axios.get('/api/maintenance'),
        axios.get('/api/users').catch(() => ({ data: [] })),
        facadeOn && hangar.productMode === 'HANGAR'
          ? axios.get('/api/mro/planning-queue', { params: queueParams }).catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] })
      ]);
      let rows = Array.isArray(woRes.data) ? woRes.data : [];
      if (!facadeOn && operatorFilter && operatorFilter !== 'ALL') {
        rows = rows.filter((wo) => String(wo.operatorId?._id || wo.operatorId || '') === String(operatorFilter));
      }
      setWorkOrders(rows);
      const planningRows = Array.isArray(planningRes?.data) ? planningRes.data : [];
      setPendingPlanning(planningRows.map((wo) => ({ ...wo, planningTask: getPlanningTask(wo) })));
      const users = Array.isArray(usersRes?.data) ? usersRes.data : [];
      setMroEngineers(users.filter((u) => u?.role === ROLES.MRO_ENGINEER));
    } catch (err) {
      console.error('MRO board load failed', err);
      setWorkOrders([]);
      setPendingPlanning([]);
      setMroEngineers([]);
    } finally {
      setLoading(false);
    }
  }, [hangarSettings.enableMroFacade, operatorFilter]);

  useEffect(() => {
    fetchWorkOrders();
  }, [fetchWorkOrders]);

  useEffect(() => {
    if (!showPendingPlanTab && pendingTab === PENDING_TAB_PLAN) {
      setPendingTab(0);
    }
  }, [showPendingPlanTab, pendingTab]);

  useEffect(() => {
    axios.get('/api/master-data/operators', { params: { isActive: 'true' } })
      .then((res) => setOperators(Array.isArray(res.data) ? res.data : []))
      .catch(() => setOperators([]));
  }, []);

  const fetchCharterAssignments = useCallback(async () => {
    try {
      setCharterLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/ops/mro-charter-assignments', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCharterAssignments(response.data || []);
    } catch (error) {
      console.error('Failed to fetch charter MRO assignments:', error);
      setCharterAssignments([]);
    } finally {
      setCharterLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCharterAssignments();
  }, [fetchCharterAssignments]);

  const openCharterTechlog = async (trip) => {
    const tlb = trip.technicalLogBookId;
    const existingId = tlb?._id || (typeof tlb === 'string' ? tlb : null);
    const tlbActive = Boolean(existingId && tlb?.isActive !== false);
    if (tlbActive) {
      navigate('/technical-log-book', { state: { openEntryId: existingId } });
      return;
    }
    try {
      setOpeningCharterTlbId(trip._id);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `/api/technical-log-book/open-for-trip/${trip._id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast('Technical Log Book opened for charter', 'success');
      await fetchCharterAssignments();
      navigate('/technical-log-book', { state: { openEntryId: response.data._id } });
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to open Technical Log Book', 'error');
    } finally {
      setOpeningCharterTlbId(null);
    }
  };

  const canAssignCharterMro = [
    ROLES.MRO_MANAGER,
    ROLES.ACCOUNTABLE_MANAGER,
    ROLES.SUPER_ADMIN
  ].includes(user?.role);

  const fetchCharterMroEngineers = useCallback(async () => {
    if (!canAssignCharterMro) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/ops/mro-engineers', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCharterMroEngineers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch MRO engineers:', error);
      setCharterMroEngineers([]);
    }
  }, [canAssignCharterMro]);

  useEffect(() => {
    fetchCharterMroEngineers();
  }, [fetchCharterMroEngineers]);

  const openCharterMroAssignDialog = (trip) => {
    const engId = trip.assignedMroEngineerId?._id || trip.assignedMroEngineerId || '';
    setCharterMroTrip(trip);
    setCharterMroForm({ assignedMroEngineerId: engId, remarks: '' });
    setCharterMroDialogOpen(true);
  };

  const assignCharterMroEngineer = async () => {
    if (!charterMroTrip || assigningCharterMro) return;
    try {
      setAssigningCharterMro(true);
      const token = localStorage.getItem('token');
      await axios.put(
        `/api/ops/trips/${charterMroTrip._id}/assign-mro-engineer`,
        {
          assignedMroEngineerId: charterMroForm.assignedMroEngineerId || null,
          remarks: charterMroForm.remarks || undefined
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast(
        charterMroForm.assignedMroEngineerId
          ? 'MRO engineer assigned for charter techlog'
          : 'MRO engineer cleared from charter',
        'success'
      );
      setCharterMroDialogOpen(false);
      setCharterMroTrip(null);
      await fetchCharterAssignments();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to assign MRO engineer', 'error');
    } finally {
      setAssigningCharterMro(false);
    }
  };

  const pipeline = useMemo(() => workOrders.filter(isInMroPipeline), [workOrders]);
  const pendingManager = useMemo(() => pipeline.filter((wo) => isPendingManagerAction(wo, user, permissions)), [pipeline, user, permissions]);
  const pendingEngineer = useMemo(() => pipeline.filter((wo) => isPendingEngineerAction(wo, user, permissions)), [pipeline, user, permissions]);
  const pendingMroQm = useMemo(() => pipeline.filter((wo) => isPendingMroQmAction(wo, user, permissions)), [pipeline, user, permissions]);
  const pendingPlanningRows = useMemo(() => pendingPlanning, [pendingPlanning]);
  const pendingTotal = pendingManager.length + pendingEngineer.length + pendingMroQm.length
    + (showPendingPlanTab ? pendingPlanningRows.length : 0);

  /** Full pipeline for managers/CAMO/etc.; engineers only see WOs awaiting their completion on the pipeline tab */
  const isMroEngineerUser = user?.role === ROLES.MRO_ENGINEER;
  const canOpenEdr = user?.role === ROLES.MRO_ENGINEER || user?.role === ROLES.SUPER_ADMIN;
  const pipelineForBenchTab = useMemo(
    () =>
      isMroEngineerUser
        ? pipeline.filter((wo) => isPendingEngineerAction(wo, user, permissions))
        : pipeline,
    [pipeline, isMroEngineerUser, user, permissions]
  );

  const statusOptions = useMemo(() => {
    const statuses = new Set(pipeline.map((wo) => wo.status || 'UNKNOWN'));
    return ['ALL', ...Array.from(statuses).sort()];
  }, [pipeline]);

  const pipelineTabStatusOptions = useMemo(() => {
    const statuses = new Set(pipelineForBenchTab.map((wo) => wo.status || 'UNKNOWN'));
    return ['ALL', ...Array.from(statuses).sort()];
  }, [pipelineForBenchTab]);

  const filteredSortedPendingManager = useMemo(() => {
    const filtered = pendingManager.filter((wo) => {
      const statusMatch = pendingManagerStatusFilter === 'ALL' || (wo.status || 'UNKNOWN') === pendingManagerStatusFilter;
      const q = normalize(pendingManagerSearch);
      if (!q) return statusMatch;
      return statusMatch && (
        normalize(woSearchText(wo)).includes(q) ||
        normalize(wo.aircraftId?.registration).includes(q) ||
        normalize(getManagerPendingLabel(wo, user, permissions)).includes(q)
      );
    });
    return sortRows(filtered, (wo) => {
      if (pendingManagerSortBy === 'aircraft') return wo.aircraftId?.registration;
      if (pendingManagerSortBy === 'status') return wo.status;
      if (pendingManagerSortBy === 'pendingAction') return getManagerPendingLabel(wo, user, permissions);
      if (pendingManagerSortBy === 'camoWorkOrder') return camoWorkOrderNumber(wo);
      return mroReferenceNumber(wo);
    }, pendingManagerSortOrder);
  }, [pendingManager, pendingManagerStatusFilter, pendingManagerSearch, pendingManagerSortBy, pendingManagerSortOrder, user, permissions]);

  const filteredSortedPendingEngineer = useMemo(() => {
    const filtered = pendingEngineer.filter((wo) => {
      const statusMatch = pendingEngineerStatusFilter === 'ALL' || (wo.status || 'UNKNOWN') === pendingEngineerStatusFilter;
      const q = normalize(pendingEngineerSearch);
      if (!q) return statusMatch;
      return statusMatch && (
        normalize(woSearchText(wo)).includes(q) ||
        normalize(wo.aircraftId?.registration).includes(q) ||
        normalize(getEngineerPendingLabel(wo, user, permissions)).includes(q)
      );
    });
    return sortRows(filtered, (wo) => {
      if (pendingEngineerSortBy === 'aircraft') return wo.aircraftId?.registration;
      if (pendingEngineerSortBy === 'status') return wo.status;
      if (pendingEngineerSortBy === 'pendingAction') return getEngineerPendingLabel(wo, user, permissions);
      if (pendingEngineerSortBy === 'camoWorkOrder') return camoWorkOrderNumber(wo);
      return mroReferenceNumber(wo);
    }, pendingEngineerSortOrder);
  }, [pendingEngineer, pendingEngineerStatusFilter, pendingEngineerSearch, pendingEngineerSortBy, pendingEngineerSortOrder, user, permissions]);

  const filteredSortedPendingMroQm = useMemo(() => {
    const filtered = pendingMroQm.filter((wo) => {
      const statusMatch = pendingMroQmStatusFilter === 'ALL' || (wo.status || 'UNKNOWN') === pendingMroQmStatusFilter;
      const q = normalize(pendingMroQmSearch);
      if (!statusMatch) return false;
      if (!q) return true;
      return (
        normalize(woDisplayLabel(wo)).includes(q) ||
        normalize(wo.aircraftId?.registration).includes(q) ||
        normalize(getMroQmPendingLabel(wo, user, permissions)).includes(q)
      );
    });
    return sortRows(filtered, (wo) => {
      if (pendingMroQmSortBy === 'aircraft') return wo.aircraftId?.registration;
      if (pendingMroQmSortBy === 'status') return wo.status;
      if (pendingMroQmSortBy === 'pendingAction') return getMroQmPendingLabel(wo, user, permissions);
      if (pendingMroQmSortBy === 'camoWorkOrder') return camoWorkOrderNumber(wo);
      return mroReferenceNumber(wo);
    }, pendingMroQmSortOrder);
  }, [pendingMroQm, pendingMroQmStatusFilter, pendingMroQmSearch, pendingMroQmSortBy, pendingMroQmSortOrder, user, permissions]);

  const filteredSortedPendingPlanning = useMemo(() => {
    const filtered = pendingPlanningRows.filter((wo) => {
      const q = normalize(pendingPlanningSearch);
      if (!q) return true;
      return (
        normalize(woSearchText(wo)).includes(q) ||
        normalize(wo.aircraftId?.registration).includes(q) ||
        normalize(wo.planningTask?.label).includes(q)
      );
    });
    return sortRows(filtered, (wo) => {
      if (pendingPlanningSortBy === 'aircraft') return wo.aircraftId?.registration;
      if (pendingPlanningSortBy === 'status') return wo.status;
      if (pendingPlanningSortBy === 'pendingAction') return wo.planningTask?.label;
      if (pendingPlanningSortBy === 'camoWorkOrder') return camoWorkOrderNumber(wo);
      return mroReferenceNumber(wo);
    }, pendingPlanningSortOrder);
  }, [pendingPlanningRows, pendingPlanningSearch, pendingPlanningSortBy, pendingPlanningSortOrder]);

  const normalizedSearch = normalize(search);
  const filteredPipeline = useMemo(() => {
    const filtered = pipelineForBenchTab.filter((wo) => {
      const statusMatch = pipelineStatusFilter === 'ALL' || (wo.status || 'UNKNOWN') === pipelineStatusFilter;
      const next = getNextAction(wo, user?.role);
      const yourTurn = Boolean(next?.canPerform);
      const turnMatch = pipelineTurnFilter === 'ALL' || (pipelineTurnFilter === 'YES' ? yourTurn : !yourTurn);
      if (!statusMatch || !turnMatch) return false;
      if (!normalizedSearch) return true;
      return (
        normalize(woSearchText(wo)).includes(normalizedSearch) ||
        normalize(wo.aircraftId?.registration).includes(normalizedSearch) ||
        normalize(next?.action).includes(normalizedSearch) ||
        normalize(wo.status).includes(normalizedSearch)
      );
    });
    return sortRows(filtered, (wo) => {
      const next = getNextAction(wo, user?.role);
      if (pipelineSortBy === 'aircraft') return wo.aircraftId?.registration;
      if (pipelineSortBy === 'status') return wo.status;
      if (pipelineSortBy === 'nextStep') return next?.action;
      if (pipelineSortBy === 'yourTurn') return next?.canPerform ? '1' : '0';
      if (pipelineSortBy === 'scheduledDate') return wo.scheduledDate || wo.dueDate;
      if (pipelineSortBy === 'camoWorkOrder') return camoWorkOrderNumber(wo);
      return mroReferenceNumber(wo);
    }, pipelineSortOrder);
  }, [pipelineForBenchTab, user?.role, pipelineStatusFilter, pipelineTurnFilter, pipelineSortBy, pipelineSortOrder, normalizedSearch]);

  const openInMaintenance = (woId) => navigate('/maintenance', { state: { selectedWorkOrderId: woId } });

  const submitManagerAction = async (wo) => {
    const form = getManagerForm(wo._id);
    if (!form.notes.trim()) {
      setManagerFormField(wo._id, 'error', 'Notes are required');
      return;
    }
    setManagerForms((prev) => ({ ...prev, [wo._id]: { ...form, loading: true, error: '' } }));
    try {
      const base = useFacade ? `/api/mro/${wo._id}` : `/api/maintenance/${wo._id}/mro`;
      const endpoint = form.decision === 'ACCEPT'
        ? `${base}/accept`
        : `${base}/reject`;
      await axios.post(endpoint, {
        notes: form.notes.trim(),
        assignedEngineerIds: (form.assignedEngineerIds || []).length
          ? form.assignedEngineerIds
          : undefined,
        currentEngineerId: (form.assignedEngineerIds || []).length
          ? (form.currentEngineerId || form.assignedEngineerIds[0])
          : undefined,
        assignedDate: form.assignedDate || undefined
      });
      showToast(`Work order ${form.decision === 'ACCEPT' ? 'accepted' : 'rejected'} successfully`, 'success');
      setManagerForms((prev) => ({ ...prev, [wo._id]: EMPTY_MANAGER_FORM }));
      await fetchWorkOrders();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to submit MRO action';
      setManagerForms((prev) => ({ ...prev, [wo._id]: { ...getManagerForm(wo._id), loading: false, error: msg } }));
      showToast(msg, 'error');
    } finally {
      setManagerForms((prev) => ({ ...prev, [wo._id]: { ...getManagerForm(wo._id), loading: false } }));
    }
  };

  const submitEngineerAction = async (wo) => {
    const form = getEngineerForm(wo._id);
    if (form.decision === 'REJECT' && !form.remarks.trim()) {
      setEngineerFormField(wo._id, 'error', 'Rejection remarks are required');
      return;
    }
    setEngineerForms((prev) => ({ ...prev, [wo._id]: { ...form, loading: true, error: '' } }));
    try {
      const endpoint = useFacade
        ? `/api/mro/${wo._id}/engineer/complete`
        : `/api/maintenance/${wo._id}/mro-engineer/complete`;
      await axios.post(endpoint, {
        decision: form.decision,
        notes: form.notes.trim() || undefined,
        remarks: form.remarks.trim() || undefined
      });
      showToast(form.decision === 'REJECT' ? 'Step rejected successfully' : 'Work marked as completed successfully', 'success');
      setEngineerForms((prev) => ({ ...prev, [wo._id]: EMPTY_ENGINEER_FORM }));
      await fetchWorkOrders();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to submit engineer action';
      setEngineerForms((prev) => ({ ...prev, [wo._id]: { ...getEngineerForm(wo._id), loading: false, error: msg } }));
      showToast(msg, 'error');
    } finally {
      setEngineerForms((prev) => ({ ...prev, [wo._id]: { ...getEngineerForm(wo._id), loading: false } }));
    }
  };

  const submitMroQmAction = async (wo, decision) => {
    const form = getMroQmForm(wo._id);
    if ((decision === 'REJECT' || decision === 'BACK_TO_ENGINEER') && !form.remarks.trim()) {
      setMroQmFormField(
        wo._id,
        'error',
        decision === 'BACK_TO_ENGINEER'
          ? 'Remarks are required when returning work to MRO engineer'
          : 'Rejection remarks are required'
      );
      return;
    }
    setMroQmForms((prev) => ({ ...prev, [wo._id]: { ...form, decision, loading: true, error: '' } }));
    try {
      const endpoint = useFacade
        ? `/api/mro/${wo._id}/qm/acknowledge-completion`
        : `/api/maintenance/${wo._id}/mro-qm/acknowledge-completion`;
      await axios.post(endpoint, {
        decision,
        notes: form.notes.trim() || undefined,
        remarks: form.remarks.trim() || undefined
      });
      const message = decision === 'BACK_TO_ENGINEER'
        ? 'Work order returned to MRO engineer'
        : decision === 'REJECT'
          ? 'Completion rejected'
          : 'Completion approved';
      showToast(message, 'success');
      setMroQmForms((prev) => ({ ...prev, [wo._id]: EMPTY_MRO_QM_FORM }));
      setMroQmDetailsById((prev) => {
        const next = { ...prev };
        delete next[wo._id];
        return next;
      });
      await fetchWorkOrders();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to submit MRO QM action';
      setMroQmForms((prev) => ({ ...prev, [wo._id]: { ...getMroQmForm(wo._id), decision, loading: false, error: msg } }));
      showToast(msg, 'error');
    } finally {
      setMroQmForms((prev) => ({ ...prev, [wo._id]: { ...getMroQmForm(wo._id), loading: false } }));
    }
  };

  const loadMroQmWorkOrderDetail = async (wo) => {
    const woId = wo?._id;
    if (!woId || mroQmDetailsById[woId] || mroQmDetailsLoading[woId]) return;
    setMroQmDetailsLoading((prev) => ({ ...prev, [woId]: true }));
    setMroQmDetailsError((prev) => ({ ...prev, [woId]: '' }));
    try {
      const response = await axios.get(`/api/maintenance/${woId}`);
      setMroQmDetailsById((prev) => ({ ...prev, [woId]: response.data }));
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load work order details';
      setMroQmDetailsError((prev) => ({ ...prev, [woId]: msg }));
    } finally {
      setMroQmDetailsLoading((prev) => ({ ...prev, [woId]: false }));
    }
  };

  const findActiveComponentByPartNumber = async (aircraftId, partNumber) => {
    const res = await axios.get('/api/components', { params: { aircraftId, isActive: true, pageSize: 500 } });
    const list = Array.isArray(res.data?.components) ? res.data.components : (Array.isArray(res.data) ? res.data : []);
    return list.find((c) => normalizePartNumber(c.partNumber) === partNumber) || null;
  };

  const applyFoundComponentToSwapForm = (woId, match) => {
    setComponentSwapForms((prev) => ({
      ...prev,
      [woId]: {
        ...getComponentSwapForm(woId),
        currentComponent: match,
        replacementPartNumber: match.partNumber || '',
        currentSerialNumber: String(match.serialNumber || '').trim(),
        replacementSerialNumber: String(match.serialNumber || '').trim(),
        replacementTimeSinceRecords: [],
        error: ''
      }
    }));
  };

  const downloadComponentPartDetailPdf = async (wo) => {
    const woId = wo?._id;
    const component = getComponentSwapForm(woId).currentComponent;
    if (!component?._id) {
      setComponentSwapFormField(woId, 'error', 'Find the current component first');
      return;
    }
    setComponentSwapForms((prev) => ({
      ...prev,
      [woId]: { ...getComponentSwapForm(woId), downloadingComponentPdf: true, error: '' }
    }));
    try {
      const safeCode = String(component.code || 'component').replace(/[^a-zA-Z0-9._-]+/g, '_');
      const safePart = String(component.partNumber || 'part').replace(/[^a-zA-Z0-9._-]+/g, '_');
      const safeSerial = String(component.serialNumber || '').trim().replace(/[^a-zA-Z0-9._-]+/g, '_');
      const filename = safeSerial
        ? `Component-Detail_${safeCode}_${safePart}_${safeSerial}.pdf`
        : `Component-Detail_${safeCode}_${safePart}.pdf`;
      await downloadAuthenticatedPdf(
        `/api/components/${component._id}/part-detail.pdf?download=1`,
        filename
      );
      showToast('A6 component tag downloaded (no operator letterpad)', 'info');
    } catch (err) {
      const msg = err.message || err.response?.data?.message || 'Failed to download component detail PDF';
      setComponentSwapFormField(woId, 'error', msg);
      showToast(msg, 'error');
    } finally {
      setComponentSwapForms((prev) => ({
        ...prev,
        [woId]: { ...getComponentSwapForm(woId), downloadingComponentPdf: false }
      }));
    }
  };

  const downloadComponentRemovalCertificatePdf = async (wo, { actionType = 'REMOVE' } = {}) => {
    const woId = wo?._id;
    const component = getComponentSwapForm(woId).currentComponent;
    if (!component?._id) {
      setComponentSwapFormField(woId, 'error', 'Find the current component first');
      return;
    }
    setComponentSwapForms((prev) => ({
      ...prev,
      [woId]: { ...getComponentSwapForm(woId), downloadingRemovalCertPdf: true, error: '' }
    }));
    try {
      const qs = new URLSearchParams({
        download: '1',
        actionType,
        workOrderId: String(woId)
      });
      const opId = getId(wo.operatorId);
      if (opId) qs.set('operatorId', opId);
      if (wo.operatorCode) qs.set('operatorCode', String(wo.operatorCode).trim());
      const safeCode = String(component.code || 'component').replace(/[^a-zA-Z0-9._-]+/g, '_');
      const safePart = String(component.partNumber || 'part').replace(/[^a-zA-Z0-9._-]+/g, '_');
      const safeSerial = String(component.serialNumber || '').trim().replace(/[^a-zA-Z0-9._-]+/g, '_');
      const filename = safeSerial
        ? `Component-Removal-Certificate_${safeCode}_${safePart}_${safeSerial}.pdf`
        : `Component-Removal-Certificate_${safeCode}_${safePart}.pdf`;
      await downloadAuthenticatedPdf(
        `/api/components/${component._id}/removal-certificate.pdf?${qs.toString()}`,
        filename
      );
      showToast('Certificate downloaded with operator letterpad', 'success');
    } catch (err) {
      const msg = err.message || err.response?.data?.message || 'Failed to download removal certificate';
      setComponentSwapFormField(woId, 'error', msg);
      showToast(msg, 'error');
    } finally {
      setComponentSwapForms((prev) => ({
        ...prev,
        [woId]: { ...getComponentSwapForm(woId), downloadingRemovalCertPdf: false }
      }));
    }
  };

  const downloadMroComponentTagPdf = async (wo) => {
    const woId = wo?._id;
    const form = getComponentSwapForm(woId);
    const component = form.currentComponent;
    if (!component?._id) {
      setComponentSwapFormField(woId, 'error', 'Find the current component first');
      return;
    }
    const tagType = form.selectedTagType || DEFAULT_MRO_COMPONENT_TAG;
    const tagMeta = MRO_COMPONENT_TAGS.find((t) => t.key === tagType) || MRO_COMPONENT_TAGS[0];
    setComponentSwapForms((prev) => ({
      ...prev,
      [woId]: { ...getComponentSwapForm(woId), downloadingMroTagPdf: true, error: '' }
    }));
    try {
      const opId = getId(wo.operatorId);
      const safePart = String(component.partNumber || 'part').replace(/[^a-zA-Z0-9._-]+/g, '_');
      const safeSerial = String(component.serialNumber || '').trim().replace(/[^a-zA-Z0-9._-]+/g, '_');

      if (tagMeta.isCertificate || tagType === 'REMOVAL_CERTIFICATE') {
        const qs = new URLSearchParams({
          download: '1',
          actionType: 'REMOVE',
          workOrderId: String(woId)
        });
        if (opId) qs.set('operatorId', opId);
        if (wo.operatorCode) qs.set('operatorCode', String(wo.operatorCode).trim());
        const filename = safeSerial
          ? `Component-Removal-Certificate_${safePart}_${safeSerial}.pdf`
          : `Component-Removal-Certificate_${safePart}.pdf`;
        await downloadAuthenticatedPdf(
          `/api/components/${component._id}/removal-certificate.pdf?${qs.toString()}`,
          filename
        );
        showToast('Removal certificate downloaded with operator letterpad', 'success');
      } else {
        const qs = new URLSearchParams({
          download: '1',
          tagType,
          workOrderId: String(woId)
        });
        if (opId) qs.set('operatorId', opId);
        if (wo.operatorCode) qs.set('operatorCode', String(wo.operatorCode).trim());
        if (form.tagReason) qs.set('reason', String(form.tagReason).trim());
        if (form.tagRemarks) qs.set('remarks', String(form.tagRemarks).trim());
        const formNo = String(tagMeta.formNo || 'TAG').replace(/[^a-zA-Z0-9._-]+/g, '_');
        const filename = safeSerial
          ? `${formNo}_${safePart}_${safeSerial}.pdf`
          : `${formNo}_${safePart}.pdf`;
        await downloadAuthenticatedPdf(
          `/api/components/${component._id}/mro-tag.pdf?${qs.toString()}`,
          filename
        );
        showToast(`${tagMeta.label} downloaded (GETS layout + operator letterpad)`, 'success');
      }
    } catch (err) {
      const msg = err.message || err.response?.data?.message || 'Failed to generate component tag PDF';
      setComponentSwapFormField(woId, 'error', msg);
      showToast(msg, 'error');
    } finally {
      setComponentSwapForms((prev) => ({
        ...prev,
        [woId]: { ...getComponentSwapForm(woId), downloadingMroTagPdf: false }
      }));
    }
  };

  const openComponentPageByPartNumber = async (wo) => {
    const woId = wo?._id;
    const form = getComponentSwapForm(woId);
    const aircraftId = getAircraftIdFromWo(wo);
    const partNumber = normalizePartNumber(form.lookupPartNumber);
    if (!aircraftId || !partNumber) {
      setComponentSwapFormField(woId, 'error', 'Aircraft and part number are required');
      return;
    }
    setComponentSwapForms((prev) => ({
      ...prev,
      [woId]: { ...getComponentSwapForm(woId), lookupLoading: true, error: '', currentComponent: null }
    }));
    try {
      const match = await findActiveComponentByPartNumber(aircraftId, partNumber);
      if (!match) {
        setComponentSwapForms((prev) => ({
          ...prev,
          [woId]: { ...getComponentSwapForm(woId), lookupLoading: false, currentComponent: null, error: 'No active component found for this part number on selected aircraft' }
        }));
        return;
      }
      applyFoundComponentToSwapForm(woId, match);
      await openComponentFormPopup({ wo, componentId: match._id, mode: 'edit' });
    } catch (err) {
      setComponentSwapForms((prev) => ({
        ...prev,
        [woId]: { ...getComponentSwapForm(woId), lookupLoading: false, error: err.response?.data?.message || 'Failed to find component' }
      }));
    } finally {
      setComponentSwapForms((prev) => ({
        ...prev,
        [woId]: { ...getComponentSwapForm(woId), lookupLoading: false }
      }));
    }
  };

  const openReplacementComponentPage = async (wo) => {
    const woId = wo?._id;
    const form = getComponentSwapForm(woId);
    const aircraftId = getAircraftIdFromWo(wo);
    const lookupPartNumber = normalizePartNumber(form.lookupPartNumber);
    let current = form.currentComponent;

    const replacementPartNumber = normalizePartNumber(form.replacementPartNumber || current?.partNumber);
    const replacementSerialNumber = String(form.replacementSerialNumber || '').trim();
    const currentSerialNumber = String(form.currentSerialNumber || current?.serialNumber || '').trim();

    if (!replacementPartNumber) {
      setComponentSwapFormField(woId, 'error', 'Replacement part number is required');
      return;
    }
    if (!replacementSerialNumber) {
      setComponentSwapFormField(woId, 'error', 'New serial number is required');
      return;
    }
    if (currentSerialNumber && replacementSerialNumber.toUpperCase() === currentSerialNumber.toUpperCase()) {
      setComponentSwapFormField(woId, 'error', 'Replacement serial number must be different from current serial number');
      return;
    }

    if (!current?._id) {
      if (!aircraftId || !lookupPartNumber) {
        setComponentSwapFormField(woId, 'error', 'Enter the current component part number first');
        return;
      }
      setComponentSwapForms((prev) => ({
        ...prev,
        [woId]: { ...getComponentSwapForm(woId), lookupLoading: true, error: '' }
      }));
      try {
        current = await findActiveComponentByPartNumber(aircraftId, lookupPartNumber);
        if (!current) {
          setComponentSwapForms((prev) => ({
            ...prev,
            [woId]: { ...getComponentSwapForm(woId), lookupLoading: false, error: 'No active component found for this part number on selected aircraft' }
          }));
          return;
        }
        applyFoundComponentToSwapForm(woId, current);
      } catch (err) {
        setComponentSwapForms((prev) => ({
          ...prev,
          [woId]: { ...getComponentSwapForm(woId), lookupLoading: false, error: err.response?.data?.message || 'Failed to find component' }
        }));
        return;
      }
    }

    setComponentSwapForms((prev) => ({
      ...prev,
      [woId]: { ...getComponentSwapForm(woId), lookupLoading: true, error: '' }
    }));
    try {
      await openComponentFormPopup({
        wo,
        componentId: current._id,
        mode: 'create',
        overrides: {
          partNumber: replacementPartNumber,
          serialNumber: replacementSerialNumber,
          workOrderNumber: woDisplayLabel(wo)
        }
      });
    } catch (err) {
      setComponentSwapFormField(woId, 'error', err.response?.data?.message || 'Failed to open replacement component form');
    } finally {
      setComponentSwapForms((prev) => ({
        ...prev,
        [woId]: { ...getComponentSwapForm(woId), lookupLoading: false }
      }));
    }
  };
  const applyFoundAddReferenceToSwapForm = (woId, match) => {
    setComponentSwapForms((prev) => ({
      ...prev,
      [woId]: {
        ...getComponentSwapForm(woId),
        addReferenceComponent: match,
        addPartNumber: match.partNumber || '',
        addSerialNumber: '',
        addTimeSinceRecords: [],
        error: ''
      }
    }));
  };

  const openAddReferenceComponentForm = async (wo) => {
    const woId = wo?._id;
    const form = getComponentSwapForm(woId);
    const aircraftId = getAircraftIdFromWo(wo);
    const partNumber = normalizePartNumber(form.addLookupPartNumber);
    if (!aircraftId || !partNumber) {
      setComponentSwapFormField(woId, 'error', 'Aircraft and reference part number are required');
      return;
    }
    setComponentSwapForms((prev) => ({
      ...prev,
      [woId]: { ...getComponentSwapForm(woId), addLookupLoading: true, error: '', addReferenceComponent: null }
    }));
    try {
      const match = await findActiveComponentByPartNumber(aircraftId, partNumber);
      if (!match) {
        setComponentSwapForms((prev) => ({
          ...prev,
          [woId]: {
            ...getComponentSwapForm(woId),
            addLookupLoading: false,
            addReferenceComponent: null,
            error: 'No active reference component found for this part number'
          }
        }));
        return;
      }
      applyFoundAddReferenceToSwapForm(woId, match);
      await openComponentFormPopup({ wo, componentId: match._id, mode: 'edit' });
    } catch (err) {
      setComponentSwapForms((prev) => ({
        ...prev,
        [woId]: {
          ...getComponentSwapForm(woId),
          addLookupLoading: false,
          error: err.response?.data?.message || 'Failed to find reference component'
        }
      }));
    } finally {
      setComponentSwapForms((prev) => ({
        ...prev,
        [woId]: { ...getComponentSwapForm(woId), addLookupLoading: false }
      }));
    }
  };

  const openAddComponentForm = async (wo) => {
    const woId = wo?._id;
    const form = getComponentSwapForm(woId);
    const aircraftId = getAircraftIdFromWo(wo);
    const lookupPartNumber = normalizePartNumber(form.addLookupPartNumber);
    let reference = form.addReferenceComponent;

    const newPartNumber = normalizePartNumber(form.addPartNumber || reference?.partNumber);
    const newSerialNumber = String(form.addSerialNumber || '').trim();

    if (!newPartNumber) {
      setComponentSwapFormField(woId, 'error', 'New component part number is required');
      return;
    }
    if (!newSerialNumber) {
      setComponentSwapFormField(woId, 'error', 'New serial number is required');
      return;
    }

    if (!reference?._id) {
      if (!aircraftId || !lookupPartNumber) {
        setComponentSwapFormField(woId, 'error', 'Enter a reference component part number first');
        return;
      }
      setComponentSwapForms((prev) => ({
        ...prev,
        [woId]: { ...getComponentSwapForm(woId), addLookupLoading: true, error: '' }
      }));
      try {
        reference = await findActiveComponentByPartNumber(aircraftId, lookupPartNumber);
        if (!reference) {
          setComponentSwapForms((prev) => ({
            ...prev,
            [woId]: {
              ...getComponentSwapForm(woId),
              addLookupLoading: false,
              error: 'No active reference component found for this part number'
            }
          }));
          return;
        }
        applyFoundAddReferenceToSwapForm(woId, reference);
      } catch (err) {
        setComponentSwapForms((prev) => ({
          ...prev,
          [woId]: {
            ...getComponentSwapForm(woId),
            addLookupLoading: false,
            error: err.response?.data?.message || 'Failed to find reference component'
          }
        }));
        return;
      }
    }

    const referenceSerial = String(reference.serialNumber || '').trim();
    if (referenceSerial && newSerialNumber.toUpperCase() === referenceSerial.toUpperCase()) {
      setComponentSwapFormField(woId, 'error', 'New serial number must be different from the reference component serial number');
      return;
    }

    setComponentSwapForms((prev) => ({
      ...prev,
      [woId]: { ...getComponentSwapForm(woId), addLookupLoading: true, error: '' }
    }));
    try {
      await openComponentFormPopup({
        wo,
        componentId: reference._id,
        mode: 'create',
        overrides: {
          partNumber: newPartNumber,
          serialNumber: newSerialNumber,
          workOrderNumber: woDisplayLabel(wo)
        }
      });
    } catch (err) {
      setComponentSwapFormField(woId, 'error', err.response?.data?.message || 'Failed to open add component form');
    } finally {
      setComponentSwapForms((prev) => ({
        ...prev,
        [woId]: { ...getComponentSwapForm(woId), addLookupLoading: false }
      }));
    }
  };

  const executeComponentSwap = async (wo, actionType) => {
    const woId = wo?._id;
    const form = getComponentSwapForm(woId);
    const current = form.currentComponent;
    if (!current?._id) {
      setComponentSwapFormField(woId, 'error', 'Find an active component first');
      return;
    }
    const replacementPartNumber = normalizePartNumber(form.replacementPartNumber || current.partNumber);
    const replacementSerialNumber = String(form.replacementSerialNumber || '').trim() || String(form.currentSerialNumber || '').trim();
    const currentSerialNumber = String(form.currentSerialNumber || '').trim();
    const replacementTimeSinceRecordsInput = Array.isArray(form.replacementTimeSinceRecords)
      ? form.replacementTimeSinceRecords
      : [];
    if (!replacementPartNumber) {
      setComponentSwapFormField(woId, 'error', 'Replacement part number is required');
      return;
    }
    if (actionType === 'REPLACE' || actionType === 'ADD') {
      if (!replacementSerialNumber) {
        setComponentSwapFormField(woId, 'error', 'New serial number is required');
        return;
      }
      if (actionType === 'REPLACE' && currentSerialNumber && replacementSerialNumber === currentSerialNumber) {
        setComponentSwapFormField(woId, 'error', 'Replacement serial number must be different from current serial number');
        return;
      }
      const invalidTimeSinceRecord = replacementTimeSinceRecordsInput.find((record) => {
        const type = String(record?.type || '').trim();
        const label = String(record?.label || '').trim();
        const elapsedValue = String(record?.elapsedTime?.value || '').trim();
        const elapsedUnit = String(record?.elapsedTime?.unit || '').trim();
        const limitValue = String(record?.limit?.value || '').trim();
        const limitUnit = String(record?.limit?.unit || '').trim();
        const remainingValue = String(record?.remainingTime?.value || '').trim();
        const remainingUnit = String(record?.remainingTime?.unit || '').trim();
        const startDate = String(record?.startDate || '').trim();
        const asOnDate = String(record?.asOnDate || '').trim();
        return !type || !label || !elapsedValue || !elapsedUnit || !limitValue || !limitUnit || !remainingValue || !remainingUnit || !startDate || !asOnDate;
      });
      if (invalidTimeSinceRecord) {
        setComponentSwapFormField(woId, 'error', 'Complete all fields in each time-since record row');
        return;
      }
    }
    setComponentSwapForms((prev) => ({
      ...prev,
      [woId]: { ...getComponentSwapForm(woId), actionLoading: true, error: '' }
    }));
    try {
      let replacementPartId = getId(current.partId);
      let replacementDescription = current.description;
      if (replacementPartNumber !== normalizePartNumber(current.partNumber)) {
        const partRes = await axios.get('/api/parts', { params: { search: replacementPartNumber, isActive: true, pageSize: 100 } });
        const parts = Array.isArray(partRes.data?.parts) ? partRes.data.parts : (Array.isArray(partRes.data) ? partRes.data : []);
        const part = parts.find((p) => normalizePartNumber(p.partNumber) === replacementPartNumber);
        if (!part?._id) {
          throw new Error(`Replacement part ${replacementPartNumber} not found in active parts catalog`);
        }
        replacementPartId = part._id;
        replacementDescription = part.description || part.partName || current.description;
      }

      const allCompRes = await axios.get('/api/components', {
        params: { aircraftId: getAircraftIdFromWo(wo), isActive: true, pageSize: 500 }
      });
      const allComps = Array.isArray(allCompRes.data?.components) ? allCompRes.data.components : (Array.isArray(allCompRes.data) ? allCompRes.data : []);
      if (actionType === 'ADD') {
        const duplicate = allComps.find((c) =>
          normalizePartNumber(c.partNumber) === replacementPartNumber
          && String(c.serialNumber || '').trim().toUpperCase() === replacementSerialNumber.toUpperCase()
        );
        if (duplicate) {
          throw new Error('This component already exists as an active component on this aircraft (same Part Number and Serial Number)');
        }
      }
      const nextCode = generateNextComponentCode(current.code, allComps.map((c) => c.code));

      let removalCertificateUrl = null;
      let removalCertificateFilename = null;
      let removalCertificateError = null;
      if (actionType === 'REMOVE' || actionType === 'REPLACE') {
        const removeRes = await axios.delete(`/api/components/${current._id}`, {
          data: {
            reason: `${actionType} by MRO/CAMO from work order ${woDisplayLabel(wo)}`,
            retireSerializedItem: actionType === 'REPLACE',
            archiveActionType: actionType,
            deactivateOnly: true,
            workOrderId: woId,
            operatorId: getId(wo.operatorId) || undefined,
            operatorCode: wo.operatorCode || undefined
          }
        });
        removalCertificateUrl = removeRes.data?.removalCertificateUrl || null;
        removalCertificateFilename = removeRes.data?.removalCertificateFilename || null;
        removalCertificateError = removeRes.data?.removalCertificateError || null;
      }

      if (actionType === 'REPLACE' || actionType === 'ADD') {
        const replacementTimeSinceRecords = replacementTimeSinceRecordsInput.map((record) => ({
          type: String(record?.type || '').trim(),
          label: String(record?.label || '').trim(),
          elapsedTime: {
            value: String(record?.elapsedTime?.value || '').trim(),
            unit: String(record?.elapsedTime?.unit || '').trim()
          },
          limit: {
            value: String(record?.limit?.value || '').trim(),
            unit: String(record?.limit?.unit || '').trim()
          },
          remainingTime: {
            value: String(record?.remainingTime?.value || '').trim(),
            unit: String(record?.remainingTime?.unit || '').trim()
          },
          startDate: new Date(record.startDate).toISOString(),
          asOnDate: new Date(record.asOnDate).toISOString()
        }));
        await axios.post('/api/components', {
          aircraftId: getAircraftIdFromWo(wo),
          assemblyId: getId(current.assemblyId),
          code: nextCode,
          ataChapterId: getId(current.ataChapterId),
          partId: replacementPartId,
          partNumber: replacementPartNumber,
          description: replacementDescription || current.description,
          serialNumber: replacementSerialNumber,
          position: current.position,
          manufacturerId: getId(current.manufacturerId),
          monitorTypeId: getId(current.monitorTypeId),
          installedOnDate: new Date().toISOString(),
          licenseId: getId(current.licenseId),
          workOrderNumber: woDisplayLabel(wo),
          notes: `${actionType} component operation from MRO workbench`,
          timeSinceRecords: replacementTimeSinceRecords
        });
      }

      if (removalCertificateError) {
        showToast(removalCertificateError, 'warning');
      } else if (removalCertificateUrl) {
        try {
          const qs = new URLSearchParams({
            download: '1',
            actionType,
            workOrderId: String(woId)
          });
          if (getId(wo.operatorId)) qs.set('operatorId', getId(wo.operatorId));
          if (wo.operatorCode) qs.set('operatorCode', String(wo.operatorCode).trim());
          await downloadAuthenticatedPdf(
            `${removalCertificateUrl}?${qs.toString()}`,
            removalCertificateFilename || `Component-Removal-Certificate_${actionType}.pdf`
          );
        } catch (certDownloadErr) {
          const msg = certDownloadErr.message || 'Removal certificate download failed';
          showToast(msg, 'warning');
        }
      }

      showToast(
        actionType === 'REMOVE'
          ? (removalCertificateUrl
            ? 'Remove completed: removal certificate downloaded (operator letterpad)'
            : removalCertificateError
              ? 'Remove completed, but no removal certificate was generated'
              : 'Remove completed: current component set inactive')
          : actionType === 'ADD'
            ? 'Add completed: new component added'
            : (removalCertificateUrl
              ? 'Replace completed: removal certificate downloaded and replacement added'
              : removalCertificateError
                ? 'Replace completed, but no removal certificate was generated'
                : 'Replace completed: old component set inactive and replacement component added'),
        removalCertificateError ? 'warning' : 'success'
      );
      setComponentSwapForms((prev) => ({ ...prev, [woId]: EMPTY_COMPONENT_SWAP_FORM }));
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to complete component remove/replace';
      setComponentSwapFormField(woId, 'error', msg);
      showToast(msg, 'error');
    } finally {
      setComponentSwapForms((prev) => ({
        ...prev,
        [woId]: { ...getComponentSwapForm(woId), actionLoading: false }
      }));
    }
  };

  const queueComponentOperation = (wo, actionType) => {
    const woId = wo?._id;
    const form = getComponentSwapForm(woId);
    const current = form.currentComponent;
    if (!current?._id) {
      setComponentSwapFormField(woId, 'error', 'Find an active component first');
      return;
    }
    const replacementPartNumber = normalizePartNumber(form.replacementPartNumber || current.partNumber);
    const replacementSerialNumber = String(form.replacementSerialNumber || '').trim() || String(form.currentSerialNumber || '').trim();
    const currentSerialNumber = String(form.currentSerialNumber || '').trim();
    const replacementTimeSinceRecordsInput = Array.isArray(form.replacementTimeSinceRecords) ? form.replacementTimeSinceRecords : [];
    if (!replacementPartNumber) {
      setComponentSwapFormField(woId, 'error', 'Replacement part number is required');
      return;
    }
    if (actionType === 'REPLACE' || actionType === 'ADD') {
      if (!replacementSerialNumber) {
        setComponentSwapFormField(woId, 'error', 'New serial number is required');
        return;
      }
      if (actionType === 'REPLACE' && currentSerialNumber && replacementSerialNumber === currentSerialNumber) {
        setComponentSwapFormField(woId, 'error', 'Replacement serial number must be different from current serial number');
        return;
      }
      const invalidTimeSinceRecord = replacementTimeSinceRecordsInput.find((record) => {
        const type = String(record?.type || '').trim();
        const label = String(record?.label || '').trim();
        const elapsedValue = String(record?.elapsedTime?.value || '').trim();
        const elapsedUnit = String(record?.elapsedTime?.unit || '').trim();
        const limitValue = String(record?.limit?.value || '').trim();
        const limitUnit = String(record?.limit?.unit || '').trim();
        const remainingValue = String(record?.remainingTime?.value || '').trim();
        const remainingUnit = String(record?.remainingTime?.unit || '').trim();
        const startDate = String(record?.startDate || '').trim();
        const asOnDate = String(record?.asOnDate || '').trim();
        return !type || !label || !elapsedValue || !elapsedUnit || !limitValue || !limitUnit || !remainingValue || !remainingUnit || !startDate || !asOnDate;
      });
      if (invalidTimeSinceRecord) {
        setComponentSwapFormField(woId, 'error', 'Complete all fields in each time-since record row');
        return;
      }
    }
    const operation = {
      actionType,
      currentComponent: { ...current },
      replacementPartNumber,
      replacementSerialNumber,
      currentSerialNumber,
      replacementTimeSinceRecords: replacementTimeSinceRecordsInput.map((record) => ({ ...record }))
    };
    setComponentSwapForms((prev) => {
      const currentForm = getComponentSwapForm(woId);
      const queue = Array.isArray(currentForm.operationQueue) ? currentForm.operationQueue : [];
      return {
        ...prev,
        [woId]: { ...currentForm, operationQueue: [...queue, operation], error: '' }
      };
    });
    showToast(`${actionType} operation added to queue`, 'success');
  };
  const buildAddOperation = (wo) => {
    const woId = wo?._id;
    const form = getComponentSwapForm(woId);
    const current = form.addReferenceComponent;
    if (!current?._id) throw new Error('Find a reference component first');
    const replacementPartNumber = normalizePartNumber(form.addPartNumber || current.partNumber);
    const replacementSerialNumber = String(form.addSerialNumber || '').trim();
    if (!replacementPartNumber) throw new Error('New component part number is required');
    if (!replacementSerialNumber) throw new Error('New serial number is required');
    const resolvedAddTimeSinceRecords = (Array.isArray(form.addTimeSinceRecords) && form.addTimeSinceRecords.length > 0)
      ? form.addTimeSinceRecords
      : (Array.isArray(current.timeSinceRecords) ? current.timeSinceRecords : []);
    const invalidTimeSinceRecord = resolvedAddTimeSinceRecords.find((record) => {
      const type = String(record?.type || '').trim();
      const label = String(record?.label || '').trim();
      const elapsedValue = String(record?.elapsedTime?.value || '').trim();
      const elapsedUnit = String(record?.elapsedTime?.unit || '').trim();
      const limitValue = String(record?.limit?.value || '').trim();
      const limitUnit = String(record?.limit?.unit || '').trim();
      const remainingValue = String(record?.remainingTime?.value || '').trim();
      const remainingUnit = String(record?.remainingTime?.unit || '').trim();
      const startDate = String(record?.startDate || '').trim();
      const asOnDate = String(record?.asOnDate || '').trim();
      return !type || !label || !elapsedValue || !elapsedUnit || !limitValue || !limitUnit || !remainingValue || !remainingUnit || !startDate || !asOnDate;
    });
    if (invalidTimeSinceRecord) throw new Error('Complete all fields in each TSN/time-since row');
    return {
      actionType: 'ADD',
      currentComponent: { ...current },
      replacementPartNumber,
      replacementSerialNumber,
      currentSerialNumber: String(current.serialNumber || '').trim(),
      replacementTimeSinceRecords: resolvedAddTimeSinceRecords.map((record) => ({ ...record }))
    };
  };
  const queueAddComponent = (wo) => {
    const woId = wo?._id;
    let op;
    try {
      op = buildAddOperation(wo);
    } catch (e) {
      setComponentSwapFormField(woId, 'error', e.message || 'Invalid add component setup');
      return;
    }
    setComponentSwapForms((prev) => {
      const currentForm = getComponentSwapForm(woId);
      const queue = Array.isArray(currentForm.operationQueue) ? currentForm.operationQueue : [];
      return { ...prev, [woId]: { ...currentForm, operationQueue: [...queue, op], error: '' } };
    });
    showToast('ADD operation added to queue', 'success');
  };

  const executeQueuedComponentOperations = async (wo) => {
    const woId = wo?._id;
    const form = getComponentSwapForm(woId);
    const queue = Array.isArray(form.operationQueue) ? form.operationQueue : [];
    if (queue.length === 0) {
      setComponentSwapFormField(woId, 'error', 'No queued operations to execute');
      return;
    }
    setComponentSwapForms((prev) => ({
      ...prev,
      [woId]: { ...getComponentSwapForm(woId), actionLoading: true, error: '' }
    }));
    try {
      for (const op of queue) {
        const current = op.currentComponent;
        let replacementPartId = getId(current.partId);
        let replacementDescription = current.description;
        if (op.replacementPartNumber !== normalizePartNumber(current.partNumber)) {
          const partRes = await axios.get('/api/parts', { params: { search: op.replacementPartNumber, isActive: true, pageSize: 100 } });
          const parts = Array.isArray(partRes.data?.parts) ? partRes.data.parts : (Array.isArray(partRes.data) ? partRes.data : []);
          const part = parts.find((p) => normalizePartNumber(p.partNumber) === op.replacementPartNumber);
          if (!part?._id) {
            throw new Error(`Replacement part ${op.replacementPartNumber} not found in active parts catalog`);
          }
          replacementPartId = part._id;
          replacementDescription = part.description || part.partName || current.description;
        }
        const allCompRes = await axios.get('/api/components', {
          params: { aircraftId: getAircraftIdFromWo(wo), isActive: true, pageSize: 500 }
        });
        const allComps = Array.isArray(allCompRes.data?.components) ? allCompRes.data.components : (Array.isArray(allCompRes.data) ? allCompRes.data : []);
        if (op.actionType === 'ADD') {
          const duplicate = allComps.find((c) =>
            normalizePartNumber(c.partNumber) === op.replacementPartNumber
            && String(c.serialNumber || '').trim().toUpperCase() === String(op.replacementSerialNumber || '').trim().toUpperCase()
          );
          if (duplicate) {
            throw new Error('Queued ADD failed: component already exists as active on this aircraft (same Part Number and Serial Number)');
          }
        }
        const nextCode = generateNextComponentCode(current.code, allComps.map((c) => c.code));

        if (op.actionType === 'REMOVE' || op.actionType === 'REPLACE') {
          const removeRes = await axios.delete(`/api/components/${current._id}`, {
            data: {
              reason: `${op.actionType} by MRO/CAMO from work order ${woDisplayLabel(wo)}`,
              retireSerializedItem: op.actionType === 'REPLACE',
              archiveActionType: op.actionType,
              deactivateOnly: true,
              workOrderId: woId,
              operatorId: getId(wo.operatorId) || undefined,
              operatorCode: wo.operatorCode || undefined
            }
          });
          const removalCertificateUrl = removeRes.data?.removalCertificateUrl || null;
          const removalCertificateFilename = removeRes.data?.removalCertificateFilename || null;
          const removalCertificateError = removeRes.data?.removalCertificateError || null;
          if (removalCertificateError) {
            showToast(removalCertificateError, 'warning');
          } else if (removalCertificateUrl) {
            try {
              const qs = new URLSearchParams({
                download: '1',
                actionType: op.actionType,
                workOrderId: String(woId)
              });
              if (getId(wo.operatorId)) qs.set('operatorId', getId(wo.operatorId));
              if (wo.operatorCode) qs.set('operatorCode', String(wo.operatorCode).trim());
              await downloadAuthenticatedPdf(
                `${removalCertificateUrl}?${qs.toString()}`,
                removalCertificateFilename || `Component-Removal-Certificate_${op.actionType}.pdf`
              );
            } catch (certDownloadErr) {
              showToast(certDownloadErr.message || 'Removal certificate download failed', 'warning');
            }
          }
        }
        if (op.actionType === 'REPLACE' || op.actionType === 'ADD') {
          const replacementTimeSinceRecords = (op.replacementTimeSinceRecords || []).map((record) => ({
            type: String(record?.type || '').trim(),
            label: String(record?.label || '').trim(),
            elapsedTime: {
              value: String(record?.elapsedTime?.value || '').trim(),
              unit: String(record?.elapsedTime?.unit || '').trim()
            },
            limit: {
              value: String(record?.limit?.value || '').trim(),
              unit: String(record?.limit?.unit || '').trim()
            },
            remainingTime: {
              value: String(record?.remainingTime?.value || '').trim(),
              unit: String(record?.remainingTime?.unit || '').trim()
            },
            startDate: new Date(record.startDate).toISOString(),
            asOnDate: new Date(record.asOnDate).toISOString()
          }));
          await axios.post('/api/components', {
            aircraftId: getAircraftIdFromWo(wo),
            assemblyId: getId(current.assemblyId),
            code: nextCode,
            ataChapterId: getId(current.ataChapterId),
            partId: replacementPartId,
            partNumber: op.replacementPartNumber,
            description: replacementDescription || current.description,
            serialNumber: op.replacementSerialNumber,
            position: current.position,
            manufacturerId: getId(current.manufacturerId),
            monitorTypeId: getId(current.monitorTypeId),
            installedOnDate: new Date().toISOString(),
            licenseId: getId(current.licenseId),
            workOrderNumber: woDisplayLabel(wo),
            notes: `${op.actionType} component operation from MRO workbench`,
            timeSinceRecords: replacementTimeSinceRecords
          });
        }
      }
      showToast(`Executed ${queue.length} queued component operations`, 'success');
      setComponentSwapForms((prev) => ({ ...prev, [woId]: EMPTY_COMPONENT_SWAP_FORM }));
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to execute queued operations';
      setComponentSwapFormField(woId, 'error', msg);
      showToast(msg, 'error');
    } finally {
      setComponentSwapForms((prev) => ({
        ...prev,
        [woId]: { ...getComponentSwapForm(woId), actionLoading: false }
      }));
    }
  };

  const uploadEngineerCrs = async (wo) => {
    const form = getEngineerForm(wo._id);
    if (!form.crsFile) {
      setEngineerFormField(wo._id, 'error', 'Please select a CRS file first');
      return;
    }
    setEngineerForms((prev) => ({ ...prev, [wo._id]: { ...getEngineerForm(wo._id), crsUploading: true, error: '' } }));
    try {
      const fd = new FormData();
      fd.append('crsFile', form.crsFile);
      await axios.post(
        useFacade ? `/api/mro/${wo._id}/engineer/crs` : `/api/maintenance/${wo._id}/mro-engineer/crs`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      showToast('CRS uploaded successfully', 'success');
      setEngineerForms((prev) => ({ ...prev, [wo._id]: { ...getEngineerForm(wo._id), crsFile: null } }));
      await fetchWorkOrders();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to upload CRS';
      setEngineerFormField(wo._id, 'error', msg);
      showToast(msg, 'error');
    } finally {
      setEngineerForms((prev) => ({ ...prev, [wo._id]: { ...getEngineerForm(wo._id), crsUploading: false } }));
    }
  };

  const downloadEngineerCrs = async (wo) => {
    try {
      const response = await axios.get(`/api/maintenance/${wo._id}/crs/download`, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', wo?.crsDocument?.originalName || `crs-${wo._id}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to download CRS file', 'error');
    }
  };

  const downloadCameWorkOrderForm = async (wo) => {
    try {
      const response = await axios.get(`/api/maintenance/${wo._id}/came-work-order-form.pdf?download=1`, {
        responseType: 'blob'
      });
      const woLabel = (wo.workOrderNumber || wo.physicalWorkOrderNumber || wo._id || 'work-order')
        .toString()
        .replace(/[^a-zA-Z0-9._-]+/g, '_');
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `CAME-FORM-04_${woLabel}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to download CAME/FORM/04 PDF', 'error');
    }
  };

  const submitCompleteAction = async (wo) => {
    try {
      await axios.put(`/api/maintenance/${wo._id}`, { status: 'COMPLETED' });
      showToast('Work order marked as completed', 'success');
      await fetchWorkOrders();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to complete work order', 'error');
    }
  };

  const renderPendingTable = (rows, labelGetter, expandedId, setExpandedId, type, onOpenEdr, onOpenWoPage2) => {
    const showEdr = typeof onOpenEdr === 'function';
    const showWoPage2 = typeof onOpenWoPage2 === 'function';
    const colSpan = 7 + (showEdr ? 1 : 0) + (showWoPage2 ? 1 : 0);
    return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell width={56} />
          <TableCell>MRO reference number</TableCell>
          <TableCell>CAMO work order</TableCell>
          <TableCell>Aircraft</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Pending action</TableCell>
          <TableCell align="right">Open</TableCell>
          {showWoPage2 && <TableCell align="right">Action Taken</TableCell>}
          {showEdr && <TableCell align="right">EDR</TableCell>}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((wo) => {
          const expanded = expandedId === wo._id;
          return (
            <React.Fragment key={wo._id}>
              <TableRow hover>
                <TableCell>
                  <IconButton size="small" onClick={() => {
                    const nextExpanded = expanded ? null : wo._id;
                    setExpandedId(nextExpanded);
                    if (nextExpanded) {
                      if (type === 'mroQm') {
                        loadMroQmWorkOrderDetail(wo);
                      } else {
                        ensureLatestApprovedTlbLoaded(wo);
                      }
                    }
                  }}>
                    {expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                  </IconButton>
                </TableCell>
                <TableCell>{renderMroReferenceCell(wo)}</TableCell>
                <TableCell>{renderCamoWorkOrderCell(wo)}</TableCell>
                <TableCell>{wo.aircraftId?.registration || '-'}</TableCell>
                <TableCell>{wo.status || '-'}</TableCell>
                <TableCell>{labelGetter(wo)}</TableCell>
                <TableCell align="right">
                  <Button size="small" variant="contained" onClick={() => openInMaintenance(wo._id)}>Open</Button>
                </TableCell>
                {showWoPage2 && (
                  <TableCell align="right">
                    <Button size="small" variant="outlined" onClick={() => onOpenWoPage2(wo)}>
                      Action Taken Form
                    </Button>
                  </TableCell>
                )}
                {showEdr && (
                  <TableCell align="right">
                    <Button size="small" variant="outlined" color="secondary" onClick={() => onOpenEdr(wo)}>
                      EDR
                    </Button>
                  </TableCell>
                )}
              </TableRow>
              <TableRow>
                <TableCell colSpan={colSpan} sx={{ py: 0, borderBottom: 0 }}>
                  <Collapse in={expanded} timeout="auto" unmountOnExit>
                    {type === 'mroQm' ? (
                      <MroQmPendingWorkOrderDetails
                        workOrder={mroQmDetailsById[wo._id] || wo}
                        loading={Boolean(mroQmDetailsLoading[wo._id])}
                        error={mroQmDetailsError[wo._id]}
                        mroQmForm={getMroQmForm(wo._id)}
                        canAct={permissions.canMROQMCompletionAck(wo)}
                        onMroQmFieldChange={setMroQmFormField}
                        onSubmitMroQmAction={submitMroQmAction}
                        onDownloadCrs={downloadEngineerCrs}
                        onDownloadCameForm={downloadCameWorkOrderForm}
                        onOpenInMaintenance={openInMaintenance}
                      />
                    ) : (
                    <PendingWorkOrderDetails
                      wo={wo}
                      user={user}
                      permissions={permissions}
                      mroEngineers={mroEngineers}
                      managerForm={getManagerForm(wo._id)}
                      engineerForm={getEngineerForm(wo._id)}
                      mroQmForm={getMroQmForm(wo._id)}
                      onManagerFieldChange={setManagerFormField}
                      onEngineerFieldChange={setEngineerFormField}
                      onMroQmFieldChange={setMroQmFormField}
                      onSubmitManagerAction={submitManagerAction}
                      onSubmitEngineerAction={submitEngineerAction}
                      onSubmitMroQmAction={submitMroQmAction}
                      onSubmitCompleteAction={submitCompleteAction}
                      onUploadEngineerCrs={uploadEngineerCrs}
                      onDownloadEngineerCrs={downloadEngineerCrs}
                      onOpenHandover={setHandoverDialogWo}
                      onOpenAssignEngineers={setAssignEngineersDialogWo}
                      componentSwapForm={getComponentSwapForm(wo._id)}
                      onComponentSwapFieldChange={setComponentSwapFormField}
                      onQueueComponentOperation={queueComponentOperation}
                      onRemoveQueuedComponentOperation={removeQueuedComponentOperation}
                      onExecuteQueuedComponentOperations={executeQueuedComponentOperations}
                      onToggleAddComponentSetup={toggleAddComponentSetup}
                      onOpenAddReferenceComponentForm={openAddReferenceComponentForm}
                      onAddComponentFieldChange={setAddComponentField}
                      onOpenAddComponentForm={openAddComponentForm}
                      onQueueAddComponent={queueAddComponent}
                      onOpenComponentPageByPartNumber={openComponentPageByPartNumber}
                      onDownloadComponentPartDetailPdf={downloadComponentPartDetailPdf}
                      onDownloadComponentRemovalCertificatePdf={downloadComponentRemovalCertificatePdf}
                      onDownloadMroComponentTagPdf={downloadMroComponentTagPdf}
                      onOpenReplacementComponentPage={openReplacementComponentPage}
                      onExecuteComponentSwap={executeComponentSwap}
                      tlbData={tlbByAircraft[getAircraftIdFromWo(wo)]}
                      tlbLoading={tlbLoadingByAircraft[getAircraftIdFromWo(wo)]}
                      tlbSaving={tlbSavingByAircraft[getAircraftIdFromWo(wo)]}
                      tlbError={tlbErrorByAircraft[getAircraftIdFromWo(wo)]}
                      onTlbFieldChange={setTlbField}
                      onSaveTlbTotals={saveTlbTotals}
                      onAmpChecksUpdated={fetchWorkOrders}
                    />
                    )}
                  </Collapse>
                </TableCell>
              </TableRow>
            </React.Fragment>
          );
        })}
        {rows.length === 0 && (
          <TableRow>
            <TableCell colSpan={colSpan}>
              {type === 'manager'
                ? 'No work orders are waiting on MRO manager actions for your account.'
                : type === 'mroQm'
                  ? 'No work orders are waiting on MRO QM actions for your account.'
                  : 'No work orders are waiting on MRO engineer actions for your account.'}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" component="h1" gutterBottom>MRO Workbench</Typography>
          <Typography variant="body2" color="text.secondary">
            Use <strong>Pending work</strong> for expandable actions, and <strong>Work orders (MRO pipeline)</strong> for the pipeline list
            (full list for MRO managers and oversight roles; MRO engineers see engineer-scoped work orders on that tab).
            Expand any work order row to view parts issued from store and quantities.
            {hangarSettings.productMode === 'HANGAR' ? ' Hangar mode: new jobs start in Planning (Operator assigned there), then enter this pipeline.' : ''}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <TextField
            select
            size="small"
            label="Operator"
            value={operatorFilter}
            onChange={(e) => setOperatorFilter(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="ALL">All operators</MenuItem>
            {operators.map((op) => (
              <MenuItem key={op._id} value={op._id}>{op.code} — {op.name}</MenuItem>
            ))}
          </TextField>
          {showPlanningCreateLink && (
            <Button startIcon={<Add />} variant="outlined" onClick={() => navigate('/planning?tab=pending')}>
              New jobs in Planning
            </Button>
          )}
          <Button startIcon={<Refresh />} variant="outlined" onClick={fetchWorkOrders} disabled={loading}>Refresh</Button>
        </Stack>
      </Stack>

      <WorkflowGuide variant="mroWorkbench" />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          <Paper sx={{ p: 0, mb: 3, overflow: 'hidden' }}>
            <Tabs
              value={benchTab}
              onChange={(_, v) => setBenchTab(v)}
              sx={{ px: 2, pt: 2, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="Pending work" />
              <Tab label="Work orders (MRO pipeline)" />
              {showCharterTab && <Tab label={`Charter techlogs (${charterAssignments.length})`} />}
            </Tabs>

            {benchTab === 0 && (
              <>
                <Stack direction="row" spacing={1} sx={{ p: 2, pb: 0 }} flexWrap="wrap" useFlexGap>
                  <Chip icon={<Assignment />} label={`${pendingTotal} pending for you`} color="primary" variant="outlined" />
                  <Chip label={`${pendingManager.length} manager`} variant="outlined" />
                  <Chip label={`${pendingEngineer.length} engineer`} variant="outlined" />
                  <Chip label={`${pendingMroQm.length} MRO QM`} variant="outlined" />
                  {showPendingPlanTab && (
                    <Chip label={`${pendingPlanningRows.length} pending plan`} variant="outlined" color="warning" />
                  )}
                  <Chip label={`${pipeline.length} in MRO pipeline (all)`} variant="outlined" />
                </Stack>
                <Box sx={{ px: 2, pt: 1 }}><Typography variant="subtitle1">Pending work</Typography></Box>
                <Tabs value={pendingTab} onChange={(_, v) => setPendingTab(v)} sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Tab label={`MRO manager (${pendingManager.length})`} />
                  <Tab label={`MRO engineer (${pendingEngineer.length})`} />
                  <Tab label={`MRO QM (${pendingMroQm.length})`} />
                  {showPendingPlanTab && (
                    <Tab label={`Pending plan (${pendingPlanningRows.length})`} />
                  )}
                </Tabs>

                <Box role="tabpanel" hidden={pendingTab !== 0} sx={{ p: 2 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
                    <TextField size="small" placeholder="Search manager list..." value={pendingManagerSearch} onChange={(e) => setPendingManagerSearch(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
                    <TextField select size="small" label="Status" value={pendingManagerStatusFilter} onChange={(e) => setPendingManagerStatusFilter(e.target.value)} sx={{ minWidth: 140 }}>
                      {statusOptions.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                    </TextField>
                    <TextField select size="small" label="Sort by" value={pendingManagerSortBy} onChange={(e) => setPendingManagerSortBy(e.target.value)} sx={{ minWidth: 160 }}>
                      <MenuItem value="workOrder">MRO reference number</MenuItem><MenuItem value="camoWorkOrder">CAMO work order</MenuItem><MenuItem value="aircraft">Aircraft</MenuItem><MenuItem value="status">Status</MenuItem><MenuItem value="pendingAction">Pending action</MenuItem>
                    </TextField>
                    <TextField select size="small" label="Order" value={pendingManagerSortOrder} onChange={(e) => setPendingManagerSortOrder(e.target.value)} sx={{ minWidth: 120 }}>
                      <MenuItem value="asc">Asc</MenuItem><MenuItem value="desc">Desc</MenuItem>
                    </TextField>
                  </Stack>
                  {filteredSortedPendingManager.length === 0 ? <Alert severity="info">No work orders are waiting on MRO manager actions for your account.</Alert> : renderPendingTable(filteredSortedPendingManager, (wo) => getManagerPendingLabel(wo, user, permissions), expandedManagerId, setExpandedManagerId, 'manager')}
                </Box>

                <Box role="tabpanel" hidden={pendingTab !== 1} sx={{ p: 2 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
                    <TextField size="small" placeholder="Search engineer list..." value={pendingEngineerSearch} onChange={(e) => setPendingEngineerSearch(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
                    <TextField select size="small" label="Status" value={pendingEngineerStatusFilter} onChange={(e) => setPendingEngineerStatusFilter(e.target.value)} sx={{ minWidth: 140 }}>
                      {statusOptions.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                    </TextField>
                    <TextField select size="small" label="Sort by" value={pendingEngineerSortBy} onChange={(e) => setPendingEngineerSortBy(e.target.value)} sx={{ minWidth: 160 }}>
                      <MenuItem value="workOrder">MRO reference number</MenuItem><MenuItem value="camoWorkOrder">CAMO work order</MenuItem><MenuItem value="aircraft">Aircraft</MenuItem><MenuItem value="status">Status</MenuItem><MenuItem value="pendingAction">Pending action</MenuItem>
                    </TextField>
                    <TextField select size="small" label="Order" value={pendingEngineerSortOrder} onChange={(e) => setPendingEngineerSortOrder(e.target.value)} sx={{ minWidth: 120 }}>
                      <MenuItem value="asc">Asc</MenuItem><MenuItem value="desc">Desc</MenuItem>
                    </TextField>
                  </Stack>
                  {filteredSortedPendingEngineer.length === 0 ? <Alert severity="info">No work orders are waiting on MRO engineer actions for your account.</Alert> : renderPendingTable(filteredSortedPendingEngineer, (wo) => getEngineerPendingLabel(wo, user, permissions), expandedEngineerId, setExpandedEngineerId, 'engineer', canOpenEdr ? openEdr : undefined, canOpenEdr ? openWoPage2 : undefined)}
                </Box>

                <Box role="tabpanel" hidden={pendingTab !== 2} sx={{ p: 2 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
                    <TextField size="small" placeholder="Search MRO QM list..." value={pendingMroQmSearch} onChange={(e) => setPendingMroQmSearch(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
                    <TextField select size="small" label="Status" value={pendingMroQmStatusFilter} onChange={(e) => setPendingMroQmStatusFilter(e.target.value)} sx={{ minWidth: 140 }}>
                      {statusOptions.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                    </TextField>
                    <TextField select size="small" label="Sort by" value={pendingMroQmSortBy} onChange={(e) => setPendingMroQmSortBy(e.target.value)} sx={{ minWidth: 160 }}>
                      <MenuItem value="workOrder">MRO reference number</MenuItem><MenuItem value="camoWorkOrder">CAMO work order</MenuItem><MenuItem value="aircraft">Aircraft</MenuItem><MenuItem value="status">Status</MenuItem><MenuItem value="pendingAction">Pending action</MenuItem>
                    </TextField>
                    <TextField select size="small" label="Order" value={pendingMroQmSortOrder} onChange={(e) => setPendingMroQmSortOrder(e.target.value)} sx={{ minWidth: 120 }}>
                      <MenuItem value="asc">Asc</MenuItem><MenuItem value="desc">Desc</MenuItem>
                    </TextField>
                  </Stack>
                  {filteredSortedPendingMroQm.length === 0 ? <Alert severity="info">No work orders are waiting on MRO QM actions for your account.</Alert> : renderPendingTable(filteredSortedPendingMroQm, (wo) => getMroQmPendingLabel(wo, user, permissions), expandedMroQmId, setExpandedMroQmId, 'mroQm')}
                </Box>

                {showPendingPlanTab && (
                  <Box role="tabpanel" hidden={pendingTab !== PENDING_TAB_PLAN} sx={{ p: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                      <Typography variant="body2" color="text.secondary">
                        Work orders awaiting planning approval before Store or MRO.
                      </Typography>
                      <Button size="small" variant="outlined" onClick={() => navigate('/planning?tab=pending')}>
                        Open full Planning page
                      </Button>
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
                      <TextField size="small" placeholder="Search pending plan..." value={pendingPlanningSearch} onChange={(e) => setPendingPlanningSearch(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
                      <TextField select size="small" label="Sort by" value={pendingPlanningSortBy} onChange={(e) => setPendingPlanningSortBy(e.target.value)} sx={{ minWidth: 160 }}>
                        <MenuItem value="workOrder">MRO reference number</MenuItem><MenuItem value="camoWorkOrder">CAMO work order</MenuItem><MenuItem value="aircraft">Aircraft</MenuItem><MenuItem value="status">Status</MenuItem><MenuItem value="pendingAction">Pending action</MenuItem>
                      </TextField>
                      <TextField select size="small" label="Order" value={pendingPlanningSortOrder} onChange={(e) => setPendingPlanningSortOrder(e.target.value)} sx={{ minWidth: 120 }}>
                        <MenuItem value="asc">Asc</MenuItem><MenuItem value="desc">Desc</MenuItem>
                      </TextField>
                    </Stack>
                    {filteredSortedPendingPlanning.length === 0 ? (
                      <Alert severity="info">No work orders are waiting on planning approval.</Alert>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell />
                              <TableCell>MRO reference number</TableCell>
                              <TableCell>CAMO work order</TableCell>
                              <TableCell>Aircraft</TableCell>
                              <TableCell>Operator</TableCell>
                              <TableCell>Scheduled</TableCell>
                              <TableCell>Task</TableCell>
                              <TableCell>Action</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filteredSortedPendingPlanning.map((wo) => (
                              <React.Fragment key={wo._id}>
                                <TableRow hover>
                                  <TableCell>
                                    <IconButton size="small" onClick={() => setExpandedPlanningId((prev) => (prev === wo._id ? null : wo._id))}>
                                      {expandedPlanningId === wo._id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                    </IconButton>
                                  </TableCell>
                                  <TableCell>{renderMroReferenceCell(wo)}</TableCell>
                                  <TableCell>{renderCamoWorkOrderCell(wo)}</TableCell>
                                  <TableCell>{wo.aircraftId?.registration || '-'}</TableCell>
                                  <TableCell>{wo.operatorCode || wo.operatorName || '-'}</TableCell>
                                  <TableCell>{formatDate(wo.scheduledDate || wo.dueDate)}</TableCell>
                                  <TableCell>
                                    <Chip size="small" color={wo.planningTask?.color || 'default'} label={wo.planningTask?.label || 'Planning'} />
                                  </TableCell>
                                  <TableCell>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => {
                                        setSelectedPlanningWo(wo);
                                        setPlanningActionOpen(true);
                                      }}
                                    >
                                      Plan
                                    </Button>
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell colSpan={8} sx={{ py: 0, borderBottom: 0 }}>
                                    <Collapse in={expandedPlanningId === wo._id} timeout="auto" unmountOnExit>
                                      <Box sx={{ py: 1 }}>
                                        <MaintenanceWorkflowStatusCard workOrder={wo} user={user} />
                                      </Box>
                                    </Collapse>
                                  </TableCell>
                                </TableRow>
                              </React.Fragment>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                )}
              </>
            )}

            {benchTab === 1 && (
              <Box sx={{ p: 2 }}>
                {isMroEngineerUser && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    This list is scoped to work orders in the MRO pipeline that need <strong>MRO engineer</strong> action from your account.
                    MRO managers and other oversight roles see the full pipeline on this tab.
                  </Alert>
                )}
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} gap={2} sx={{ mb: 2 }}>
                  <Box>
                    <Typography variant="subtitle1">Work orders (MRO pipeline)</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {isMroEngineerUser
                        ? 'Awaiting engineer completion on your account (search and filters apply to this list).'
                        : 'All work orders currently in the MRO pipeline for your organization.'}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip label={`${pipelineForBenchTab.length} work order${pipelineForBenchTab.length === 1 ? '' : 's'}`} variant="outlined" />
                  </Stack>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                  <TextField size="small" placeholder="Search WO, aircraft, next step..." value={search} onChange={(e) => setSearch(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} sx={{ minWidth: 260 }} />
                  <TextField select size="small" label="Status" value={pipelineStatusFilter} onChange={(e) => setPipelineStatusFilter(e.target.value)} sx={{ minWidth: 130 }}>{pipelineTabStatusOptions.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}</TextField>
                  <TextField select size="small" label="Your turn" value={pipelineTurnFilter} onChange={(e) => setPipelineTurnFilter(e.target.value)} sx={{ minWidth: 120 }}>
                    <MenuItem value="ALL">All</MenuItem><MenuItem value="YES">Yes</MenuItem><MenuItem value="NO">No</MenuItem>
                  </TextField>
                  <TextField select size="small" label="Sort by" value={pipelineSortBy} onChange={(e) => setPipelineSortBy(e.target.value)} sx={{ minWidth: 150 }}>
                    <MenuItem value="scheduledDate">Scheduled</MenuItem><MenuItem value="workOrder">MRO reference number</MenuItem><MenuItem value="camoWorkOrder">CAMO work order</MenuItem><MenuItem value="aircraft">Aircraft</MenuItem><MenuItem value="status">Status</MenuItem><MenuItem value="nextStep">Next step</MenuItem><MenuItem value="yourTurn">Your turn</MenuItem>
                  </TextField>
                  <TextField select size="small" label="Order" value={pipelineSortOrder} onChange={(e) => setPipelineSortOrder(e.target.value)} sx={{ minWidth: 120 }}>
                    <MenuItem value="asc">Asc</MenuItem><MenuItem value="desc">Desc</MenuItem>
                  </TextField>
                </Stack>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width={56} />
                      <TableCell>MRO reference number</TableCell>
                      <TableCell>CAMO work order</TableCell>
                      <TableCell>Aircraft</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Scheduled</TableCell>
                      <TableCell>Next step</TableCell>
                      <TableCell>Your turn</TableCell>
                      <TableCell align="right">Open</TableCell>
                      {canOpenEdr && <TableCell align="right">Action Taken</TableCell>}
                      {canOpenEdr && <TableCell align="right">EDR</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredPipeline.map((wo) => {
                      const next = getNextAction(wo, user?.role);
                      const yourTurn = Boolean(next?.canPerform);
                      const expanded = expandedPipelineId === wo._id;
                      const pipelineColSpan = canOpenEdr ? 11 : 9;
                      return (
                        <React.Fragment key={wo._id}>
                          <TableRow hover sx={yourTurn ? { bgcolor: 'action.selected' } : undefined}>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => setExpandedPipelineId(expanded ? null : wo._id)}
                              >
                                {expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                              </IconButton>
                            </TableCell>
                            <TableCell>{renderMroReferenceCell(wo)}</TableCell>
                            <TableCell>{renderCamoWorkOrderCell(wo)}</TableCell>
                            <TableCell>{wo.aircraftId?.registration || '-'}</TableCell>
                            <TableCell>{wo.status || '-'}</TableCell>
                            <TableCell>{formatDate(wo.scheduledDate || wo.dueDate)}</TableCell>
                            <TableCell>{next?.action || '-'}</TableCell>
                            <TableCell>{yourTurn ? <Chip size="small" label="Yes" color="warning" /> : <Chip size="small" label="No" variant="outlined" />}</TableCell>
                            <TableCell align="right">
                              <Button size="small" variant="outlined" onClick={() => openInMaintenance(wo._id)}>Open</Button>
                            </TableCell>
                            {canOpenEdr && (
                              <TableCell align="right">
                                <Button size="small" variant="outlined" onClick={() => openWoPage2(wo)}>Action Taken Form</Button>
                              </TableCell>
                            )}
                            {canOpenEdr && (
                              <TableCell align="right">
                                <Button size="small" variant="outlined" color="secondary" onClick={() => openEdr(wo)}>EDR</Button>
                              </TableCell>
                            )}
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={pipelineColSpan} sx={{ py: 0, borderBottom: 0 }}>
                              <Collapse in={expanded} timeout="auto" unmountOnExit>
                                <Box sx={{ py: 1 }}>
                                  <WorkOrderIssuedPartsPanel workOrderId={wo._id} />
                                  {Array.isArray(wo.ampChecks) && wo.ampChecks.length > 0 && (
                                    <Box sx={{ mt: 1.5, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                      <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        spacing={1}
                                        alignItems={{ xs: 'stretch', sm: 'center' }}
                                        justifyContent="space-between"
                                        sx={{ mb: 1 }}
                                      >
                                        <Typography variant="subtitle2">
                                          Checklist for MRO Engineer ({wo.ampChecks.filter((c) => c.status === 'COMPLETED').length}/{wo.ampChecks.length} completed — required before engineer complete)
                                        </Typography>
                                        {permissions.canMROHandover?.(wo) && wo.ampChecks.some((c) => c.status !== 'COMPLETED') && (
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            color="secondary"
                                            onClick={() => setHandoverDialogWo(wo)}
                                            sx={{ flexShrink: 0 }}
                                          >
                                            Hand over at checks
                                          </Button>
                                        )}
                                      </Stack>
                                      <AMPChecksTab
                                        workOrder={wo}
                                        onUpdate={fetchWorkOrders}
                                        user={user}
                                        title="Checklist"
                                      />
                                    </Box>
                                  )}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                    {filteredPipeline.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={canOpenEdr ? 11 : 9}>
                          {pipelineForBenchTab.length === 0
                            ? (isMroEngineerUser
                              ? 'No work orders in the MRO pipeline are awaiting engineer completion from you right now.'
                              : 'No work orders are in the MRO pipeline yet.')
                            : 'No rows match your search/filter.'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            )}

            {benchTab === 2 && showCharterTab && (
              <Box sx={{ p: 2 }}>
                <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center">
                  <Typography variant="subtitle1" sx={{ flex: 1 }}>
                    Charter Technical Log Book assignments
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<Refresh />}
                    variant="outlined"
                    onClick={fetchCharterAssignments}
                    disabled={charterLoading}
                  >
                    Refresh
                  </Button>
                </Stack>
                {charterLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Charter</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Engineer</TableCell>
                          <TableCell>Techlog</TableCell>
                          <TableCell>Sectors</TableCell>
                          <TableCell align="right">Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {charterAssignments.map((trip) => {
                          const tlb = trip.technicalLogBookId;
                          const tlbId = tlb?._id || (typeof tlb === 'string' ? tlb : null);
                          const tlbActive = Boolean(tlbId && tlb?.isActive !== false);
                          const eng = trip.assignedMroEngineerId;
                          const engName = eng
                            ? `${eng.firstName || ''} ${eng.lastName || ''}`.trim() || eng.email
                            : '-';
                          return (
                            <TableRow key={trip._id} hover>
                              <TableCell>
                                {trip.charterNumber || trip.title}
                                <Typography variant="caption" display="block" color="text.secondary">
                                  {trip.title}
                                </Typography>
                              </TableCell>
                              <TableCell>{trip.status}</TableCell>
                              <TableCell>{engName}</TableCell>
                              <TableCell>
                                {tlbActive
                                  ? `Open (${tlb?.workflowStatus || 'DRAFT'})`
                                  : 'Not opened'}
                              </TableCell>
                              <TableCell>{(trip.sectors || []).length}</TableCell>
                              <TableCell align="right">
                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                  {canAssignCharterMro && (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      startIcon={<Engineering />}
                                      onClick={() => openCharterMroAssignDialog(trip)}
                                    >
                                      Assign
                                    </Button>
                                  )}
                                  <Button
                                    size="small"
                                    variant="contained"
                                    disabled={
                                      openingCharterTlbId === trip._id
                                      || (!tlbActive && !(trip.sectors || []).some((s) => s.aircraftId))
                                    }
                                    onClick={() => openCharterTechlog(trip)}
                                  >
                                    {tlbActive
                                      ? 'Continue techlog'
                                      : openingCharterTlbId === trip._id
                                        ? 'Opening…'
                                        : 'Open techlog'}
                                  </Button>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        {charterAssignments.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={6}>
                              No active charter techlog assignments.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}

          </Paper>
        </>
      )}
      {edrOpen && edrWorkOrder && (
        <EngineeringDefectReportDialog open={edrOpen} onClose={closeEdr} workOrder={edrWorkOrder} user={user} />
      )}
      {woPage2Open && woPage2WorkOrder && (
        <WorkOrderSecondPageDialog
          open={woPage2Open}
          onClose={closeWoPage2}
          workOrder={woPage2WorkOrder}
          user={user}
          onSaved={async ({ workOrderId }) => {
            await fetchWorkOrders();
            if (workOrderId) {
              try {
                const res = await axios.get(`/api/maintenance/${workOrderId}`);
                setWoPage2WorkOrder(res.data);
              } catch (err) {
                console.error('Failed to refresh work order after Action Taken Form save', err);
              }
            }
          }}
        />
      )}
      <ComponentForm
        open={componentFormOpen}
        onClose={closeComponentForm}
        selectedComponent={componentFormSelected}
        selectedAircraft={componentFormAircraft}
        formData={componentFormData}
        setFormData={setComponentFormData}
        dropdownOptions={componentFormDropdowns}
        onSubmit={handleMroComponentFormSubmit}
        user={user}
        assemblies={componentFormAssemblies}
        onRefreshDropdowns={fetchComponentFormDropdowns}
        readOnly={false}
        currentUsageTotals={componentFormLiveUsage?.currentUsageTotals || null}
        effectiveTlbUsageMapping={componentFormLiveUsage?.effectiveTlbUsageMapping ?? null}
      />

      <Dialog
        open={charterMroDialogOpen}
        onClose={() => !assigningCharterMro && setCharterMroDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Assign MRO Engineer
          {charterMroTrip
            ? ` — ${charterMroTrip.charterNumber || charterMroTrip.title}`
            : ''}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Assign an MRO engineer to open and maintain the charter Technical Log Book.
            If a previous techlog was deleted, they can open a new one for the same charter.
          </Typography>
          <FormControl fullWidth margin="normal">
            <InputLabel>MRO Engineer</InputLabel>
            <Select
              label="MRO Engineer"
              value={charterMroForm.assignedMroEngineerId || ''}
              onChange={(e) => setCharterMroForm((prev) => ({
                ...prev,
                assignedMroEngineerId: e.target.value
              }))}
            >
              <MenuItem value="">
                <em>None (clear)</em>
              </MenuItem>
              {charterMroEngineers.map((eng) => (
                <MenuItem key={eng._id} value={eng._id}>
                  {`${eng.firstName || ''} ${eng.lastName || ''}`.trim() || eng.email}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            margin="normal"
            label="Remarks (optional)"
            value={charterMroForm.remarks}
            onChange={(e) => setCharterMroForm((prev) => ({ ...prev, remarks: e.target.value }))}
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCharterMroDialogOpen(false)} disabled={assigningCharterMro}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={assignCharterMroEngineer}
            disabled={assigningCharterMro}
          >
            {assigningCharterMro ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <PlanningActions
        open={planningActionOpen}
        onClose={() => {
          setPlanningActionOpen(false);
          setSelectedPlanningWo(null);
        }}
        workOrder={selectedPlanningWo}
        onSuccess={() => {
          setPlanningActionOpen(false);
          setSelectedPlanningWo(null);
          fetchWorkOrders();
        }}
      />

      <MROHandoverDialog
        open={Boolean(handoverDialogWo)}
        workOrder={handoverDialogWo}
        mroEngineers={mroEngineers}
        useFacade={useFacade}
        onClose={() => setHandoverDialogWo(null)}
        onSuccess={() => {
          setHandoverDialogWo(null);
          fetchWorkOrders();
        }}
      />
      <MROAssignEngineersDialog
        open={Boolean(assignEngineersDialogWo)}
        workOrder={assignEngineersDialogWo}
        mroEngineers={mroEngineers}
        useFacade={useFacade}
        onClose={() => setAssignEngineersDialogWo(null)}
        onSuccess={() => {
          setAssignEngineersDialogWo(null);
          fetchWorkOrders();
        }}
      />
    </Box>
  );
};

export default MroBoard;
